import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

import { benchmarksDe, benchmarksEn } from './messages/benchmarks'
import { checksDe, checksEn } from './messages/checks'
import { collectionsDe, collectionsEn } from './messages/collections'
import { catalogDe, catalogEn } from './messages/catalog'
import { labelingDe, labelingEn } from './messages/labeling'
import { queryDe, queryEn } from './messages/query'
import { quickTagDe, quickTagEn } from './messages/quickTag'
import { contributionsDe, contributionsEn } from './messages/contributions'
import { dictationDe, dictationEn } from './messages/dictation'
import { cloudDe, cloudEn } from './messages/cloud'
import { educationDe, educationEn } from './messages/education'
import { impactDe, impactEn } from './messages/impact'
import { forumDe, forumEn } from './messages/forum'
import { schedulingDe, schedulingEn } from './messages/scheduling'
import { studyTagsDe, studyTagsEn } from './messages/studyTags'
import { peopleDe, peopleEn } from './messages/people'
import { sitesDe, sitesEn } from './messages/sites'
import { maskJoinDe, maskJoinEn } from './messages/maskJoin'
import { inferenceDe, inferenceEn } from './messages/inference'
import { uploadsDe, uploadsEn } from './messages/uploads'
import { readStored, writeStored } from './stored'
import type { SiteRole, Specialty } from './types'

const en = {
  researchOnly: 'For research use only — not for diagnosis',
  appTagline: 'Pediatric fracture research',
  mainNavigation: 'Main navigation',
  menu: 'Menu',
  navWork: 'Work',
  navManage: 'Manage',
  navPlatform: 'Platform',
  language: 'Language',
  version: 'Version',
  unread: 'unread',
  loading: 'Loading…',
  save: 'Save',
  cancel: 'Cancel',
  confirm: 'Confirm',
  remove: 'Remove',
  add: 'Add',
  create: 'Create',
  logout: 'Log out',
  profile: 'Profile',
  sites: 'Sites',
  notFoundTitle: 'Page not found',
  manualContents: 'Contents',
  notFound: 'This page does not exist, or you may not open it.',
  name: 'Name',
  email: 'E-mail',
  role: 'Role',
  none: 'None yet.',
  copy: 'Copy',
  copied: 'Copied',
  copyFailed: 'Not copied: select it and copy',
  loginFailed: 'The login could not be completed. Please try again.',
  accountInactive: 'Your account is deactivated. Please contact your site admin.',
  loginAgain: 'Log in',
  sessionEnded: 'Your session has ended. Log in again to continue.',

  experienceNote:
    'Stored with every annotation set, so reader experience can be analyzed. Seen by you and whoever runs your jobs or courses; by others only in aggregates.',
  experienceRequired: 'Needed before you start',
  experienceContinue: 'Save and continue',
  specialty: 'Specialty',
  chooseSpecialty: 'Choose your specialty',
  readingSince: 'Reading trauma radiographs since',
  accountSecurity: 'Name, password and sign-in',

  description: 'Description',
  ethicsReference: 'Ethics approval reference',
  ethicsValidUntil: 'Ethics approval valid until',
  site: 'Site',
  unknownSite: 'Another site',
  members: 'Members',
  searchUser: 'Search by name, username or e-mail (at least 3 characters)',
  details: 'Details',
  pageSections: 'Sections of this page',
  tabAgreement: 'Agreement',
  tabAccess: 'Sign-in and support',

  newSite: 'New site',
  siteCode: 'Code (lowercase letters, digits, hyphens)',
  siteName: 'Site name',
  invite: 'Invite by e-mail',
  firstName: 'First name',
  lastName: 'Last name',
  invitationSent: 'Invitation sent.',

  // The sites workspace: the user's sites beside the token check, and the sections of the site open.
  people: 'People',
  canUpload: 'Can upload',
  peopleCanUpload: '{enrolled} of {total} can upload',
  membersCount: '{count} members',
  membersCount_one: '1 member',
  ready: 'Ready',
  dpaNoEnd: 'no end date',
  storageNotActive: '{count} locations not active',
  storageNotActive_one: '1 location not active',
  sharesWaiting: '{count} waiting for approval',
  sharesWaiting_one: '1 waiting for approval',
  loginConnected: 'Login with the site’s identity provider connected',
  ssoConnected: 'Connected',
  state_ok: 'in order',
  state_warn: 'needs attention',
  yourAccess: 'Your access',
  uploadToken: 'Upload token',
  enrolledForSite: 'You are enrolled for this site’s token. Check the token you were given with the token check.',
  notEnrolledForSite: 'You are not enrolled for this site’s token yet. Ask a site admin of {site} to enroll you.',
  platformView: 'Platform admin view',
  yourToken: 'Your token',
  waitingCount: '{count} waiting',
  uploadsBlocked: 'Uploads blocked',
  secondFactorRules: 'Second-factor rules',
  storageLocations: 'Locations',
  storageDefault: 'Default for new uploads',
  storageProcessing: 'Processing on the platform',
  storageProcessingHint: "Lets the platform's servers render views and thumbnails of the images here, compute their duplicate fingerprints and run models on them. Without it, those functions are unavailable for these images; uploads are still validated and files still reach authorized users.",
  storageHostedHint: 'Provided by the platform, always available',
  storageAlwaysOn: 'Always on',
  storageUsePerLocation: 'Use per location',
  storageUsePerCollection: 'Use per site collection',
  storageMoves: 'Moving images',
  storageMovesHint:
    "Moves the site's images to another of its locations: all of them, or those a collection holds now. They move one by one in the background and keep their annotation sets, first readings and resolutions. One move runs at a time.",
  storageMoveWhat: 'Images',
  storageMoveAll: "All of the site's images",
  storageMoveSiteCollections: "The site's collections",
  storageMoveOwnCollections: 'Your collections',
  storageMoveCollection: '{name} ({count} images)',
  storageMoveCollection_one: '{name} (1 image)',
  storageMoveTo: 'Move to',
  storageMoveTarget: 'To',
  storageMove: 'Move',
  storageMoveConfirm: 'Move to {target}: {what}?',
  storageMoveProgress: 'Moved so far: {what}',
  storageMoveProgressHead: 'Moved',
  storageMoveStarted: 'Started',
  storageMoveUnseen: 'A collection',
  move_pending: 'Waiting',
  move_running: 'Moving',
  move_done: 'Done',
  move_failed: 'Failed',
  move_cancelled: 'Cancelled',
  storageLocation: 'Location',
  sharingPolicyHint: 'What the site allows for its data leaving it.',
  outsideAccessHint: 'Who from outside the site opened its collections, jobs or frozen collections.',

  tokenFromGenerator:
    'The key officer creates the site’s token with the key generator and gives you its recovery sheet. Enter the token and the certificate from the sheet. Only the key ID and the certificate are sent.',
  tokenLostWarning:
    'A lost token cannot be recovered. Keep the recovery sheet locked away at the site. A new token comes only from the key officer and gives returning patients new pseudonyms.',
  keyCertificate: 'Certificate from the key generator',
  certificateInvalid: 'This is not a certificate from the key generator.',
  certificateOtherToken: 'This certificate belongs to another token.',
  keyGeneratedNo: 'Token no. {serial}',
  keyAcceptedNo: 'Token no. {serial}, made earlier and accepted',
  token: 'Token (64 hexadecimal characters)',
  tokenInvalid: 'A token has exactly 64 hexadecimal characters.',
  alreadyAnonymized: 'Anonymized already',
  filesOfOtherSite: 'Some files were anonymized before with another site’s token, or one you are not enrolled for. The platform rejects them in this upload.',
  keyLabel: 'Label',
  keyId: 'Key ID',
  registerKey: 'Register key ID',
  retireKey: 'Retire token',
  retireWarning: 'No uploads will be accepted with this token any more.',

  anonymizationToken: 'Anonymization token',
  tokenPanelIntro: 'Uploads anonymize with your site’s token. It is checked in this browser and never sent anywhere; the platform only learns its key ID.',
  tokenSet: 'Token set',
  tokenNotSet: 'No token set',
  tokenNotRecognized: 'Token not recognized',
  tokenChecking: 'Checking token…',
  tokenLocked: 'Files are being checked or uploaded with this token. Change it once they are done or paused.',
  forgetToken: 'Forget token',
  setToken: 'Set token',
  uploadNeedsToken: 'Set your site’s token to check and upload files.',
  tokenMatches: 'This token belongs to {site}. You are enrolled to upload with it.',
  tokenNoMatch: 'This token does not match any token you are enrolled for. Check it for typos.',

  images: 'Images',
  any: 'Any',
  bodyPart: 'Body part',
  laterality: 'Laterality',
  view: 'View',
  sex: 'Sex',
  age: 'Age (years)',
  minAge: 'Minimum age (years)',
  maxAge: 'Maximum age (years)',
  left: 'Left',
  right: 'Right',
  both: 'Both',
  female: 'Female',
  male: 'Male',
  otherSex: 'Other',
  pseudonym: 'Patient pseudonym',
  shiftedDate: 'Shifted date',
  imageCount: '{count} images',
  imageCount_one: '1 image',
  previous: 'Previous',
  next: 'Next',
  loadingImage: 'Loading image…',
  dimensions: 'Size',
  sameImageIn: 'The same image is stored {count} more times',
  sameImageIn_one: 'The same image is stored once more',
  similarImages: '{count} similar images elsewhere',
  similarImages_one: 'A similar image elsewhere',
  similarEvidence: 'agreement {agreement}, coverage {coverage}',
  similarInverted: 'grey values reversed',
  similarFiles: 'Files similar to images on the platform ({count})',
  similarIsEvidence: 'Found by the OXF-RI fingerprint. This is evidence, not a verdict: compare the images before you act on it.',
  fit: 'Fit',
  invert: 'Invert',
  clahe: 'CLAHE',
  edges: 'Sharpen 200%',
  claheHelp: 'Contrast enhancement (CLAHE): raises the contrast region by region. For display only, never saved with the image.',
  edgesHelp: 'Unsharp masking at 200%: sharpens fine detail such as cortices and fracture lines, noise included, and can draw bright or dark rims along edges. For display only, never saved with the image.',
  crop: 'Crop',
  cropHelp: 'Crop view: images with a crop box show only what it encloses, such as the image without its padding. For display only; the image stays whole.',
  enhancedHelp: 'Changed for display only. The image itself is unchanged.',
  resetWindow: 'Reset window',
  saveWindow: 'Save window',
  saveWindowHelp: 'Opens this image with the current window/level for everyone and records it with the annotations. Other DICOM viewers keep the file’s own window.',
  windowSaved: 'Saved window/level',
  windowSavedHelp: 'This image opens with its saved window/level.',
  adjustWindowBeforeSaving: 'Adjust the window before saving a review presentation. The original display curve is preserved until then.',
  imageViewport: 'Image viewport',
  confirmPersonalAuthorized: 'I am authorized to upload and store these images on the platform.',
  selectedStudies: 'Selected studies',
  studiesSelected: '{count} studies selected',
  studiesSelected_one: '1 study selected',
  chooseStudy: 'Select the study of {image}',
  privateStudy: 'private',
  keepStudyPrivate: 'Keep private',
  shareStudiesWithSite: 'Share with site',
  keepStudyPrivateHint: 'Only you can open a private study. Otherwise the other members of your site can open and annotate it.',
  deleteStudies: 'Delete {count} studies',
  deleteStudies_one: 'Delete 1 study',
  deleteStudiesWarning: 'Every image of these studies is deleted with its annotations.',
  clearSelection: 'Clear selection',
  tooManyStudies: 'Select at most {count} studies at a time.',
  notEnrolled: 'You are not enrolled for a site token. Ask your site admin to enroll you before uploading.',
  chooseFiles: 'Choose DICOM files or ZIP archives',
  chooseFolder: 'Choose a folder',
  chooseImage: 'Choose an image',
  dropHint: 'You can also drop files and folders here.',
  rememberToken: 'Remember the token in this browser for {days} days. Anyone using this browser profile can then use it.',
  pauseUpload: 'Pause',
  resumeUpload: 'Resume: send the other {count} files',
  resumeUpload_one: 'Resume: send the other file',
  reviewTitle: 'Images before upload',
  reviewSummary: '{count} files ready',
  reviewSummary_one: '1 file ready',
  reviewBurnedIn: '{count} state burned-in annotation, usually a side marker',
  reviewBurnedIn_one: '1 states burned-in annotation, usually a side marker',
  reviewBurnedInBadge: 'Burned-in annotation',
  reviewIssuers: 'The files name different Issuers of Patient ID',
  reviewInstitutions: 'The files name different institutions',
  reviewOriginsIntro: 'Images from another hospital whose patient IDs your archive replaced with the site’s belong to the site’s patients and stay. Leave out files that still carry another hospital’s patient IDs.',
  reviewNoneNamed: 'None named',
  reviewGroupFiles: '{count} files',
  reviewGroupFiles_one: '1 file',
  reviewLeaveOutGroup: 'Leave these out',
  reviewIdentity: 'Patient IDs to check',
  reviewIdentityIntro: 'These files are sent unless you leave them out, since they can be correct. A patient ID that lost its leading zeros gives a child a second pseudonym: correct the archive’s export, or leave the files out.',
  reviewLeadingZeros: 'Patient IDs that differ only in leading zeros',
  reviewRecordsDiffer: 'One patient ID with different birth dates or names',
  identityLeaveOutIntro: 'Or leave the ticked files out: the other files are checked again without them.',
  identityLeaveOut: 'Leave {count} files out',
  identityLeaveOut_one: 'Leave 1 file out',
  reviewShow: 'Show the images',
  reviewHide: 'Hide the images',
  reviewRange: '{from}–{to} of {count}',
  reviewIntro: 'Each image is shown as it will be uploaded, with the text the file carries; those with burned-in annotation come first. Click an image to leave its file out, and again to take it back.',
  reviewPreparing: 'Anonymizing these files for viewing…',
  reviewToggle: 'Upload {name}',
  reviewFullSize: 'Full size',
  reviewTexts: 'Text in the file',
  reviewDetails: 'Pseudonym and file name',
  why_review: 'Left out while looking through the images before upload: click its image there to take it back.',
  outputName: 'File name',
  correctionsIntro: 'What you enter stays in this browser and is used only to anonymize the chosen files. Empty fields change nothing.',
  correctionsApply: 'Apply and check again',
  engineDownloading: 'Loading the anonymization engine (about 20 MB, only the first time)…',
  engineStarting: 'Starting the anonymization engine…',
  studies: 'Studies',
  accepted: 'Accepted',
  duplicates: 'Already uploaded',
  rejected: 'Rejected',
  inProgress: 'In progress',
  confirmNoBurnedInText: 'I checked the images as far as I considered necessary: they contain no burned-in identifying text.',
  burnedInLeaveOut: 'Leave out the {count} files with burned-in annotation',
  burnedInLeaveOut_one: 'Leave out the file with burned-in annotation',
  anonymizeAndUpload: 'Anonymize and upload {count} files',
  anonymizeAndUpload_one: 'Anonymize and upload 1 file',
  file: 'File',
  status: 'Status',
  reason: 'Reason',
  why_not_dicom: 'Not a DICOM file. Only DICOM files can be uploaded.',
  why_unreadable: 'The file cannot be read: it was moved or changed after choosing it, or the archive is damaged or encrypted.',
  why_burned_in: 'Left out: the file states that the image carries burned-in annotation.',
  why_unsupported: 'This image type is not supported: only single-frame grayscale radiographs (CR, DX).',
  why_transfer: 'The file did not arrive completely. Send it again.',
  why_damaged: 'The image data is damaged or was changed on the way.',
  why_identity: 'The patient ID is missing or invalid, so no pseudonym can be made.',
  why_patient_id_placeholder: 'The patient ID repeats one character, as placeholders for unknown patients do, and would join every unknown patient into one. The file is never changed: enter the patient ID the site gave this patient, or leave the file out.',
  why_identity_uids: 'The same study, series or image appears under different patient IDs, so it is unclear whose it is. Correct the patient ID of the files that carry the wrong one, or leave them out; the others are then checked again.',
  why_identity_records: 'The files of one patient ID name different patients: their birth dates and their names both differ. The ID may be a placeholder or given twice. Correct the patient ID of the files that belong to another patient, or leave them out.',
  why_site_setup: 'The site has not set its time zone yet. A site admin sets it on the site’s page, beside the site token.',
  why_token_required: 'This file still needs anonymizing: enter the site’s token.',
  why_key_not_enrolled: 'The file was anonymized with a token you are not enrolled for.',
  why_key_other_site: 'The file was anonymized with another site’s token than the one of this upload.',
  why_reference_unresolved: 'The file refers to images this upload does not contain, as a stitched image refers to the exposures it was made from, which are usually not archived. It was not uploaded. Upload it without those references.',
  omitReferencesIntro: 'References to images not in this upload are removed from the anonymized files; references to images in it are kept, and the images themselves stay as they are.',
  omitReferences: 'Upload without those references',
  showImage: 'Show image',
  imagePreparing: 'Preparing…',
  imageCannotShow: 'Cannot be shown',
  why_reference: 'A reference to another DICOM object cannot be safely anonymized. The file was not uploaded. The referenced objects or a reviewed decision to omit optional references are needed.',
  uploadHistoryHint: 'These counts cover files sent to the platform. Files held back before sending are listed with their upload under Uploads while this tab stays open.',
  why_agreement: 'The site that anonymized the file has no valid data processing agreement.',
  why_foreign: 'Anonymized elsewhere, for example by the standalone app or an earlier version. Upload the originals, or the files an importer delivers in the platform\'s format.',
  why_record: 'The file’s anonymization record is missing or invalid. Anonymize the original file again.',
  why_unsafe_metadata: 'The file contains unexpected metadata that may identify a patient. Anonymize the original file again.',
  why_retained_characteristic: 'This file contains patient characteristics the anonymization profile cannot safely retain. Leave it out and contact your administrator.',
  why_presentation: 'The image names a display curve (VOI LUT Function) the platform does not know. Leave it out, or export it from the modality with a linear window instead.',
  why_foreign_labels: 'The annotations embedded in the file use labels the catalog lacks. Map them to catalog labels.',
  why_annotations: 'The annotations embedded in the file are invalid.',
  why_conflict: 'The file’s metadata contradict each other, for example body part, side, view or dates.',
  why_missing_value: 'A required value, such as body part, side, view or acquisition date, is missing or not recognized.',
  why_invalid_value: 'A value in the file is invalid, for example the age, the view or the window.',
  why_validation_failed: 'Checking this file failed repeatedly on the server. Please tell the platform’s admins.',
  why_worker: 'Anonymizing in the browser failed, for example for lack of memory. Check the files again.',
  why_interrupted: 'Interrupted. Check the files again to send the rest.',
  why_other: 'The file cannot be used as it is.',

  reading: 'Reading',
  myAssignments: 'My assignments',
  myContributions: 'My contributions · {site}',
  noAssignments: 'No assignments yet',
  noOpenAssignments: 'All your assignments are finished',
  noAssignmentsHint: 'When a coordinator gives you studies to read, the assignment appears here with its progress, and you open it from here. Meanwhile you can open any image you may see and annotate it in your own set.',
  readingAside: 'About reading',
  yourContributions: 'Your contributions',
  yourContributionsHint: 'Your place among each site’s readers, by the studies you completed.',
  readingHow: 'How reading goes',
  readingHowAssignments: 'An assignment holds the studies a coordinator gave you, with the labels and the instructions to read them by.',
  readingHowFinish: 'Your annotations are saved as you go; mark each image finished when you are done with it.',
  readingHowCompared: 'Finished readings are compared with the other readers’ and their differences resolved.',
  finishedAssignments: 'Finished assignments ({count})',
  job: 'Job',
  jobs: 'Jobs',
  newJob: 'New job',
  jobName: 'Job name',
  readersPerImage: 'Readers per image',
  assignmentSize: 'Images per assignment',
  readers: 'Readers',
  noReaders: 'Add members with the owner, manager or annotator role first.',
  selection: 'Image selection',
  selectionHint: 'Every image matching these filters is included; a study is never split across assignments.',
  fromCollection: 'From a collection',
  conditions: 'Annotations and reading',
  condition: 'Condition',
  addCondition: 'Add condition',
  conditionPresence: 'With or without',
  conditionWith: 'With',
  conditionWithout: 'Without',
  conditionKind: 'What',
  conditionLabel: 'the label',
  conditionAnyLabel: 'any label',
  conditionAssigned: 'an assignment',
  conditionFinished: 'a finished read',
  conditionResolved: 'a resolution',
  conditionBy: 'Whose',
  byMe: 'by me',
  byAnyone: 'by anyone',
  byAnyoneHint: '“By anyone” counts the annotations, reads and resolutions of everyone whose work you may see: on your sites’ studies and those granted to you, never on images you read blinded.',
  unpaired: 'Unpaired',
  collections: 'Collections',
  newCollection: 'New collection',
  saveCollection: 'Save collection',
  deleteCollection: 'Delete collection',
  collectionsIntro: 'A collection holds studies you may open: those that match its filters now, those you put into it, or, frozen, those it held when it was frozen. It never copies an image and gives nobody access to anything.',
  createJob: 'Create job',
  created: 'Created',
  progress: 'Progress',
  finishedOf: '{finished} / {total}',
  openJob: 'Open',
  job_new: 'New',
  job_in_progress: 'In progress',
  job_finished: 'Finished',
  job_returned: 'Returned',
  reading_finished: 'Finished',
  readingSummary: '{finished} finished · {progress} in progress · {open} open',
  histogram: 'The image’s pixel values, with the window over them',
  atDetector: 'at the detector',
  viewN: 'View {index}',
  filterFrom: '{name} from {value}',
  maskTool: 'Mask tool',
  brush_paint: 'Paint',
  brush_erase: 'Erase',
  brush_outline: 'Outline',
  brushSize: 'Brush size',
  newMask: 'New mask',
  aiSuggestions: 'AI suggestions',
  aiModels: 'AI models',
  registerModel: 'Register a model version',
  modelService: 'Model service',
  modelKey: 'Model file in the service',
  loadFromService: 'Load kind and classes from the service',
  modelVersion: 'Version',
  modelKind: 'Kind',
  modelClasses: 'Classes (comma separated)',
  inputWindow: 'Input window',
  window_percentile: 'Percentiles (0.5–99.5 %)',
  window_dicom: 'DICOM window',
  window_minmax: 'Full range',
  maxInputSize: 'Longest input side in pixels (0: original)',
  intendedUse: 'Intended use',
  license: 'License',
  kind_detection: 'Detection',
  kind_segmentation: 'Segmentation',
  kind_classification: 'Classification',
  kind_interactive: 'Interactive segmentation',
  draft_restored: 'Unsaved edits from your last session were restored.',
  focusMode: 'Focus',
  reviews: 'Reviews',
  notifications: 'Notifications',
  markAllRead: 'Mark all as read',
  emailNotifications: 'Also by e-mail',
  emailNotificationsIntro: 'E-mail is reserved for security notices and notifications that need action. Upload results and resolved platform problems appear here only. Security notices are always sent.',
  kind_job_assigned: 'An assignment is given to me',
  kind_job_returned: 'An assignment of mine is returned with comments',
  kind_adjudication_assigned: 'Discrepancies are assigned to me',
  kind_upload_finished: 'My upload has finished',
  kind_ethics_expiring: "My site's ethics approval is about to end",
  kind_ethics_ended: "My site's ethics approval has ended",
  notice_job_assigned: '{site}: new assignment in "{task}" (images: {images})',
  notice_job_returned: '{site}: "{task}" was returned (comments: {comments})',
  notice_adjudication_assigned: '{site}: you adjudicate "{task}"',
  notice_upload_finished: '{site}: upload finished, {accepted} accepted, {duplicates} already uploaded, {rejected} rejected',
  notice_ethics_expiring: '{site}: the ethics approval ends on {until}',
  notice_ethics_ended: '{site}: the ethics approval ended on {until}',
  kind_ops_alert: 'A platform problem is found',
  kind_ops_resolved: 'A platform problem is resolved',
  notice_ops_alert: 'Alert: {en}',
  notice_ops_resolved: 'Resolved: {en}',
  platformStatus: 'Status',
  services: 'Services',
  serviceUp: 'Up',
  serviceDown: 'Down',
  noGpu: 'No GPU',
  queues: 'Queues',
  queue_uploads: 'Files waiting for validation',
  queue_exports: 'Exports waiting or building',
  queue_model_runs: 'Model runs waiting or running',
  queue_fingerprints: 'Images waiting for their duplicate fingerprint',
  queue_failed_emails: 'E-mails that could not be delivered',
  queue_file_deletions: 'File deletions waiting for their storage',
  queue_ledger_entries: 'Restore-ledger entries not yet written',
  storageUse: 'Storage',
  storage: 'Storage',
  sharing: 'Sharing',
  importsFound: 'Annotation sets in these files: {sets}, {pending} waiting for your decision',
  importNoAuthor: 'Without an author',
  importTo: 'Whose sets are these',
  importExternal: 'An external reader of this name',
  importNobody: 'No one: leave them out',
  importAssign: 'Assign',
  import_pending: 'waiting',
  import_imported: 'imported',
  import_conflict: 'the reader already had a set',
  import_invalid: 'labels do not fit the label catalog',
  import_discarded: 'left out',
  import_changed: 'change your own set',
  proposedChanges: 'The files change your own annotations here:',
  acceptChanges: 'Accept the changes',
  discardChanges: 'Discard them',
  secondFactor: 'Second factor',
  secondFactorHint: 'Users these rules cover set up an authenticator app at their next login, before the platform opens.',
  secondFactorFor: 'Required for',
  secondFactorAll: 'All users',
  secondFactorSite: 'Members of {site}',
  secondFactorRole: 'Everyone who is {role}',
  secondFactorRequire: 'Require',
  ssoTitle: 'Login with the site’s identity provider',
  ssoHint: 'Members log in with their hospital account through the site’s OpenID Connect provider, such as Entra ID; the provider decides who may. Register the redirect URI below there. Existing accounts are linked after one login with the platform password.',
  ssoIssuer: 'Provider',
  ssoRedirect: 'Redirect URI',
  ssoName: 'Name on the login page',
  ssoDiscovery: 'Discovery address',
  ssoClient: 'Client ID',
  ssoSecret: 'Client secret',
  ssoSecretKept: 'Leave empty to keep the stored secret.',
  ssoConnect: 'Connect',
  ssoDisconnect: 'Disconnect',
  supportAccess: 'Support access',
  supportHint: 'Platform admins see no image data of the site unless you allow it here: read only, for 30 days at most, and recorded in the audit log.',
  grantSupport: 'Allow access',
  platformAdmin: 'Platform admin',
  labels: 'Labels',
  labelType: 'Type',
  type_tag: 'Image tag',
  type_rectangle: 'Rectangle',
  boxRotation: 'Rotation (°)',
  boxRotationHint: 'Clockwise. Drag the outer handle or enter an angle.',
  boxRotationInvalid: 'Use 0–360° and keep all corners inside the image.',
  type_polygon: 'Polygon',
  type_polyline: 'Polyline',
  type_points: 'Points',
  type_mask: 'Mask',
  labelColor: 'Color',
  labelShortcut: 'Key',
  labelNameHint: 'new_label_name',
  addLabel: 'Add label',
  attributeValues: 'Values',
  attributeValuesHint: 'choices, or minimum, maximum, step',
  attributeDefault: 'Default',
  addAttribute: 'Add attribute',
  confirmLabelChange: 'Change them anyway',
  guidelines: 'Annotation guidelines',
  guidelinesVersion: 'Guidelines, version {version}',
  guidelinesVersions: 'Versions',
  guidelinesNone: 'There are no guidelines yet.',
  guidelinesEdit: 'Write a new version',
  guidelinesWrite: 'Write guidelines',
  guidelinesText: 'Text',
  guidelinesHint: 'Markdown: # headings, - lists, **bold**. Every save is a new version; annotation sets record the version they were made under.',
  guidelinesImage: 'Add an example image',
  guidelinesImageHint: 'PNG, JPEG or WebP up to 5 MB, without any identifying data.',
  guidelinesSave: 'Save as new version',
  retentionUntil: 'Keep the data until',
  retentionExpiring: "The retention period ends on {date} (in {days} days): extend it with a reason, have the site's token destroyed, or delete the site's data.",
  retentionExpired: "The retention period has ended: extend it with a reason, have the site's token destroyed, or delete the site's data. Nothing is deleted by itself.",
  retentionReason: 'Reason',
  retentionReasonHint: 'Why the data is kept until then, for example the study protocol. Recorded in the audit log.',
  kind_retention_ending: 'My site’s retention period is about to end',
  notice_retention_ending: '{site}: the retention period ends on {until}',
  myRoles: 'My roles',
  allow_site_users: 'manage the site’s users',
  allow_site_storage: 'manage storage',
  allow_site_keys: 'manage the anonymization token',
  allow_site_sharing: 'decide on sharing',
  allow_site_audit: 'view the audit log',
  allow_site_inference: 'manage the site’s inference keys',
  allow_site_upload: 'upload the site’s studies',
  allow_site_annotate: 'open and annotate the studies the site and its grants show, and work on assignments',
  allow_site_coordinate: 'organize the site’s collections, jobs, frozen collections, benchmarks and teaching releases',
  allow_platform_sites: 'manage sites, platform settings, models and the status page',
  allow_platform_inference: 'grant sites the inference service and release model versions for it',
  allow_platform_appoint: 'appoint educators and catalog editors',
  allow_catalog: 'maintain the label catalog and the annotation guidelines',
  allow_education_teach: 'create courses, invite learners, offer case sets and follow the learners’ progress',
  allow_education_learn: 'read the case sets offered to them',
  allow_job_read: 'read the studies of their assignment and complete it',
  allow_job_review: 'check completed assignments and return them with comments',
  allow_job_adjudicate: 'resolve the discrepancies assigned to them',
  apiTokens: 'API tokens',
  apiTokensIntro: 'For scripts and the command-line upload. A token acts as you, never with more rights, and until its last day. The API is documented at /api/docs.',
  apiTokenShownOnce: 'Copy the token now: it is shown only this once.',
  tokenScope: 'Rights',
  tokenRead: 'Read only',
  tokenWrite: 'Everything I may do',
  tokenNameHint: 'For example: command-line upload on my laptop',
  tokenName: 'Name of the new token',
  tokenLastDay: 'Last day',
  tokenUntil: 'until {date}',
  tokenLastUsed: 'last used {date}',
  createApiToken: 'Create token',
  deviceTitle: 'Connect the command-line upload',
  deviceCode: 'Code shown by pedfx-upload',
  deviceContinue: 'Continue',
  deviceAsks: '{client} asks to upload and work in your name.',
  deviceWarning: 'Confirm only if you started this on your own computer and pedfx-upload shows this code. Nobody from the platform will ever ask you for it.',
  deviceApprove: 'Connect',
  deviceDeny: 'Refuse',
  deviceApproved: 'Connected. pedfx-upload continues on its own; you can close this page. You can disconnect it under API tokens in your profile.',
  deviceDenied: 'Refused. pedfx-upload is not connected.',
  privacyNotice: 'Privacy notice',
  imprint: 'Imprint',
  termsOfUse: 'Terms of use',
  legalTexts: 'Legal',
  userManual: 'User manual',
  acceptTermsOfUse: 'I accept the terms of use',
  outsideAccess: 'Opened from outside the site',
  opened_task: 'opened the job',
  opened_dataset: 'opened the frozen collection',
  opened_benchmark: 'downloaded benchmark cases',
  opened_collection: 'opened the collection',
  split: 'Split',
  withExport: 'shared with you, with export',
  viewOnly: 'shared with you, view only',
  annotationSet: 'Annotation set',
  shareToUser: 'A user',
  shareToSite: 'A whole site',
  openDataset: 'Open',
  sharingPolicy: 'Data of the site leaving it',
  policy_forbidden: 'Not allowed',
  policy_approval: 'Only with the approval of a site admin',
  policy_allowed: 'Allowed',
  approve: 'Approve',
  refuse: 'Refuse',
  sharedWithMe: 'Shared with me',
  share: 'Share',
  shareValidUntil: 'Last day of access (optional)',
  revoke: 'Revoke',
  recipient: 'Recipient',
  shareState_pending_approval: 'Awaiting approval',
  shareState_refused: 'Refused',
  shareState_awaiting_acceptance: 'Awaiting acceptance',
  shareState_active: 'Active',
  shareState_expired: 'Expired',
  shareState_revoked: 'Revoked',
  dataUseTerms: 'Data use terms',
  acceptTerms: 'Accept the data use terms',
  sharedUntil: 'shared until {date}',
  kind_grant_received: 'Studies are granted to me',
  siteData: 'The site\'s data',
  siteLeaving: 'The site leaves the platform. From {date} on, the platform deletes its images and everything derived from them.',
  siteDataDeleting: 'The site\'s data is being deleted since {date}.',
  siteDataDeleted: 'The site\'s data was deleted on {date}.',
  siteAnnotationsHint: 'Every annotation set, first reading and resolution on the site\'s studies, image by image, as JSON.',
  siteAnnotationsDownload: 'Download the site\'s annotations',
  siteDataDelete: 'Delete all of the site\'s data',
  siteDataDeleteHint: 'Deletes the site\'s images with every annotation, first reading, resolution and prediction on them; they leave the frozen collections that hold them. Download the annotations first. This cannot be undone.',
  typeNameToConfirm: 'Type “{name}” to confirm',
  siteTokenDestroy: 'Record that the token was destroyed',
  tokenDestroyed: 'token destroyed',
  siteTokenDestroyHint: 'Record it once the key officer has destroyed the token and its recovery sheet, and the enrolled users have deleted their copies. Its key ID is retired: no upload is accepted with the token any more, and browsers forget it when they next open the platform. The images stay usable, and their pseudonyms can no longer be derived from patient IDs. This answers the retention reminders.',
  siteTokenDestroyed: "The site's token was destroyed, recorded on {date}. The images stay usable; their pseudonyms can no longer be derived from patient IDs.",
  siteNoticeUntil: 'Notice period until',
  siteRecordDeparture: 'Record that the site leaves',
  siteCancelDeparture: 'The site stays',
  kind_site_departure: 'My site leaves the platform',
  notice_site_departure: '{site} leaves the platform: download its annotations before {until}',
  kind_teaching_approval: 'A teaching release awaits my site’s approval',
  notice_teaching_approval: '{dataset} is released for teaching and holds images of {site}: approve or refuse it',
  kind_model_released: 'A model version is released for the inference service',
  notice_model_released: '{model} {version} is released for the inference service',
  kind_model_retired: 'A model version leaves the inference service',
  notice_model_retired: '{model} {version} is no longer available from the inference service',
  notice_grant_received: '{site} granted you its studies',
  grants: 'Grants',
  grantsHint: "Share your site's studies with another site, or named users there, to view or to annotate. Recipients accept the terms and see rendered views only; withdrawing ends access at once.",
  grantsReceived: 'Grants received',
  grantEveryone: 'every member',
  grantNoEnd: 'no end date',
  grantAccess: 'Access',
  grantView: 'View',
  grantAnnotate: 'View and annotate',
  grantExport: 'View and export',
  grantWithdrawn: 'Withdrawn',
  grantExpired: 'Expired',
  grantWithdraw: 'Withdraw',
  grantWithdrawWarning: 'Access ends at once.',
  grantUsers: 'Named users (optional)',
  grantUsersHint: 'E-mail addresses of members of that site; none grants every member.',
  grantAgreement: 'Data sharing agreement',
  grantAgreementHint: 'The agreement this grant rests on, for example its reference and date.',
  grantTransferBasis: 'Legal basis for the transfer',
  grantTransferBasisHint: 'The site is outside the EEA or has no recorded country: name the basis under GDPR articles 44 to 49, for example standard contractual clauses.',
  grantCreate: 'Grant',
  grantForbidden: "The sharing policy keeps the site's data at the site, so it grants nothing.",
  grantFrom: 'Studies of {site}',
  grantOpen: 'Open on the images page',
  grantRenderedNotice: 'You see these studies as views rendered on the server, marked for you.',
  siteCountry: 'Country',
  siteCountryHint: 'Two-letter code, for example AT. Grants to sites outside the EEA need a transfer basis.',
  notice_account_locked: 'Your account was blocked for a while after repeated failed logins',
  storageHosted: 'Hosted storage of the platform',
  storageUploadsGo: 'New uploads go to',
  storageEndpoint: 'S3 endpoint',
  storageRegion: 'Region',
  storageBucket: 'Bucket',
  storagePrefix: 'Folder (optional, ending in /)',
  storageAccessKey: 'Access key ID',
  storageSecret: 'Secret access key',
  storageCredentialsHint: 'Stored encrypted and never shown again.',
  storageTestAndAdd: 'Test and add',
  storageTestAgain: 'Test in the browser again',
  storageReplace: 'Replace credentials',
  connectorHint: 'A site connector runs in your site’s network next to its storage (a folder, such as a NAS, or an S3-compatible bucket) and keeps an outbound connection to the platform. The storage’s credentials stay with the connector; images only pass through the platform. Create a one-time code, run the command on a host with Docker, then test and activate the new location here.',
  connectorCode: 'Create a registration code',
  connectorRun: 'Run on the connector’s host, within a day; the code works once:',
  connectorEnvironment: 'with a file connector.env next to it that names the storage:',
  connectorLocation: 'Site connector',
  connectorConnected: 'connected',
  connectorOffline: 'not connected',
  connectorNone: 'no connector',
  connectorReplace: 'Replace connector',
  connectorReplaceCode: 'Create a code for the new connector',
  connectorRevoke: 'Revoke connector',
  storageActive: 'Active since {date}',
  storageInactive: 'Not active: the browser check is missing',
  storageLost: 'Declared lost on {date}',
  storageCopy: 'Point at a copy',
  storageCopyHint: 'For a copy of this bucket that the site made, also of a lost one. The platform checks the copy like a new bucket and switches only once it holds the file of every image here; the browser check follows. Choose another upload location first.',
  storageCopySwitch: 'Check the copy and switch',
  storageCopyMissing: 'The copy lacks {count} image files. Copy them too, then try again.',
  storageDeclareLost: 'Declare lost',
  storageFound: 'Found again',
  storageLostWarning: 'Its images stay listed with their annotations but open as lost until they are uploaded again. Uploads stop going there.',
  storageLostConfirm: "Type the location's name to confirm",
  storageStep_write: 'write',
  storageStep_read: 'read',
  storageStep_list: 'list',
  storageStep_private: 'not public',
  storageStep_delete: 'delete',
  storageBrowserFailed: 'Your browser could not write to the bucket and read from it. Add the CORS rule below to the bucket and test again.',
  storageCors: 'Browsers load images directly from the bucket, so it needs this CORS rule:',
  storageNoKey: 'Linking a site’s own bucket needs a storage key on the server (PEDFX_STORAGE_KEY).',
  storageSize: 'Size',
  storageSummary: '{images} images; uploads {uploads}, exports {exports}.',
  diskFree: '{free} free of {total}',
  certificateUntil: 'certificate valid until {until}',
  lastSeen: 'last reported {when}',
  backups: 'Backups',
  backupOk: 'Last backup succeeded',
  backupFailed: 'Last backup failed',
  backupNone: 'No backup yet',
  lastBackup: 'Last successful backup',
  lastRestoreCheck: 'Last restore check',
  lastLedgerCopy: 'Restore ledger last copied off-site',
  patientLookup: 'Find or delete a patient',
  patientLookupIntro: 'Enter the original patient ID exactly as in the DICOM files, and the site token. The pseudonym is computed in this browser; only it is sent to the platform.',
  siteToken: 'Site token',
  patientWarnings: 'Patients to review',
  patientAssociations: 'Patient IDs of one patient',
  patientAssociationsIntro: 'Associate two patient IDs that belong to the same patient, for example after the hospital merged them. Both pseudonyms and the difference of their date shifts are computed in this browser; only they are sent. Stored files stay as they are; splits, a patient’s other studies, counts and lookups follow the association.',
  associationFirst: 'Patient ID',
  associationSecond: 'Patient ID of the same patient',
  associate: 'Associate',
  dissociate: 'Undo',
  patientWarningsIntro: 'The images of these patients disagree. Check them against the archive and correct the metadata where it is wrong; the platform changes nothing by itself.',
  patientWarningSex: 'sexes {sexes}',
  patientWarningBirthDate: 'different birth dates',
  patientId: 'Patient ID',
  findPatient: 'Find images',
  patientWithToken: 'With the token',
  patientWithoutToken: 'Without the token',
  patientWithoutTokenIntro: 'Choose the patient’s images from your archive, for example their original DICOM files. This browser computes a digest of each image’s pixels, which the platform never changes; only the digests are sent, and the site’s images with the same pixels lead to the patient.',
  patientArchiveImages: 'Choose archive images',
  patientImagesUnread: 'None of these files could be read as a DICOM image.',
  patientImagesSkipped: 'Files that could not be read as DICOM images: {count}',
  tokenNotOfSite: "This token is not one of the site's tokens.",
  patientFound: '{count} images of this patient at this site. Pseudonym',
  patientFound_one: '1 image of this patient at this site. Pseudonym',
  deletePatient: 'Delete all {count} images and their annotations',
  deletePatient_one: 'Delete the image and its annotations',
  deletePatientWarning: 'Deletes the files, annotations and predictions of these images, which leave the frozen collections holding them, and the exports that held them. This cannot be undone.',
  patientDeleted: 'Deleted {images} images; they left {datasets} frozen collections.',
  patientFilesWaiting: 'The files of {count} images are at a storage location that does not answer. They are deleted as soon as it answers again.',
  dashboard: 'Dashboard',
  studiesPerSite: 'Studies per site',
  assignmentsPerJob: 'Assignments per job',
  agreementPerJob: 'Agreement between readers: open a job.',
  readerProgress: 'Readers',
  readingTime: 'Average reading time per finished image',
  perImage: 'image',
  annotationsPerLabel: 'Annotations per label',
  dpa: 'Data processing agreement',
  dpaValid: 'Valid: uploads allowed',
  dpaMissing: 'Missing or ended: uploads are blocked',
  dpaVersion: 'Version',
  dpaSignedOn: 'Signed on',
  dpaValidUntil: 'Valid until (optional)',
  ethicsExpired: 'The ethics approval has ended. Nothing is blocked: record its renewal here.',
  ethicsExpiring: 'The ethics approval ends on {date}, in {days} days.',
  auditLog: 'Audit log',
  auditSite: "This site's entries.",
  auditAll: 'All entries of the platform.',
  auditActionFilter: 'Action, e.g. upload. or collection.open',
  filter: 'Filter',
  exportCsv: 'Export CSV',
  verifyChain: 'Verify hash chain',
  chainIntact: 'The hash chain is intact: no entry was changed or removed.',
  chainBroken: 'The hash chain breaks at entry {id}.',
  when: 'When',
  who: 'Who',
  auditAction: 'Action',
  auditTarget: 'Target',
  loadOlder: 'Load older entries',
  showMore: 'Show {count} more',
  andMore: '+{count} more',
  onlyDisagreeing: 'Only images where readers disagree',
  correctMetadata: 'Correct metadata',
  applyToStudy: 'Apply to all {count} views of the study',
  history: 'Change history',
  change_added: 'added {labels}',
  change_changed: 'changed {labels}',
  change_removed: 'removed {labels}',
  change_tagged: 'tagged {labels}',
  change_untagged: 'cleared {labels}',
  patientStudies: "This patient's studies",
  studyNumber: 'Study {number}',
  backToStudy: 'Back to study {number}',
  priorStudiesSetting: "Readers may open their patients' earlier and later studies",
  freeTextSetting: 'Readers may name free-text classes for findings no label names',
  blindedSetting: 'Blinded reading: until they finish their assignment, readers see nobody else\'s annotations on its images. Switched off, they see a heatmap of the boxes of everyone who finished an image',
  aiSuggestionsSetting: 'AI suggestions for readers',
  aiOff: 'Off',
  aiOnRequest: 'On request',
  aiAtOnce: 'Shown at once',
  aiAtOnceBadge: 'AI suggestions shown at once',
  jobReading: 'Reading',
  jobLabels: 'Labels offered ({count})',
  jobLabelsHint: 'In the job, readers add only these labels; the annotations they already have stay. Without a choice the job offers every label.',
  jobInstructions: 'Instructions for the readers',
  freshReaders: 'Give studies to readers who have not annotated them before, where enough are left',
  annotatedBefore: '{count} studies annotated before',
  cannotOpen: 'cannot open {count} studies',
  cannotOpen_one: 'cannot open 1 study',
  whichStudies: 'Which studies',
  earlierWork: 'Annotated before the job',
  coReader: 'Co-reader {n}',
  deleteJob: 'Delete job',
  endJob: 'Close or delete the job',
  closeJob: 'Close job',
  reopenJob: 'Open the job again',
  closeJobHint: 'A closed job is read-only: its readers no longer change or finish their assignments, and its reviews and resolutions stay as they are. Its blinding and other reading rules no longer hold.',
  jobClosed: 'Closed',
  deleteJobHint: 'Removes the assignments, first readings and resolutions of this job. The studies, every reader\'s annotations and the collections frozen from it stay.',
  deleteJobConfirm: 'Delete "{name}" for good',
  labelCatalog: 'Label catalog',
  labelCatalogIntro: 'One catalog of labels for every site, job, case set and labeling profile, kept by the catalog editors. Jobs and case sets offer parts of it. A label in use keeps its name and type for good and is retired instead of removed; a retired label that nothing uses or names can be deleted. Missing a label? Propose it here, and name a free-text class in the tool meanwhile.',
  catalogEditor: 'Catalog editor',
  catalogEditors: 'Catalog editors',
  catalogEditorsHint: 'Catalog editors add and change labels and write the annotation guidelines. Add people by the e-mail address of their account.',
  addCatalogEditor: 'Add catalog editor',
  jobLabelsOffered: 'Labels: {labels}',
  allLabels: 'all',
  blindedBadge: 'Blinded until finished',
  noAiBadge: 'Without AI suggestions',
  noPriorsBadge: 'Without prior studies',
  noFreeTextBadge: 'Without free-text classes',
  offsetSameDay: 'Same day',
  offsetUnknown: 'Time unknown',
  offsetEarlier: '{amount} earlier',
  offsetLater: '{amount} later',
  offsetDays: '{count} d',
  offsetWeeks: '{count} wk',
  offsetMonths: '{count} mo',
  offsetYears: '{count} y',
  review: 'Review',
  reader: 'Reader',
  noFindings: 'no findings',
  importedSet: 'imported',
  importedFrom: 'imported from {author}',
  reviewComments: 'Review comments',
  clickToComment: 'Click a point of the image to pin a comment there.',
  comment: 'Comment',
  addComment: 'Add comment',
  returnToReader: 'Return to reader ({count} comments)',
  returnToReader_one: 'Return to reader (1 comment)',
  returnHelp: 'The reader sees the comments; the commented images open for changes.',
  assignmentReturnedNotice: 'A reviewer returned this assignment with comments. Only the commented images are open again.',
  focusHelp: 'Shows only the images and the drawing tools; the same key switches back.',
  draft_discarded: 'Unsaved edits from an earlier session were made on an older version and were discarded.',
  modelRuns: 'Model runs',
  modelRunsIntro: 'Runs a model over every image of the job in the background. Readers still see its suggestions only when they ask, then without waiting.',
  siteRunsIntro: 'Runs a model in the background over every study you may open, as they are when it starts; a collection’s page runs one over its studies. Readers still see its suggestions only when they ask, then without waiting.',
  collectionRunsIntro: 'Runs a model in the background over the images of the saved collection at the site you choose, as they are when it starts. Readers still see its suggestions only when they ask.',
  noBatchModels: 'No model for runs in the background is enabled.',
  runAllImages: 'all images',
  runSelectedImages: 'selected images',
  startCollectionRun: 'Run on the collection’s images',
  noThumbnail: 'No preview',
  imageUnavailable: 'Unavailable: its site’s storage does not answer',
  retryThumbnails: 'Retry thumbnails',
  imageAlt: 'Image',
  ageShort: '{age} y',
  saveAsCollection: 'Save as collection',
  saveAsCollectionHint: 'Keep these filters as a new collection',
  clearFilters: 'Clear filters',
  noImagesMatch: 'No images match these filters.',
  overlays: 'Labels on the images',
  groupBy: 'Group by',
  groupPatient: 'Patient',
  groupPatientHint: 'Each patient’s images in a light frame',
  groupStudy: 'Study',
  groupStudyHint: 'Each study’s images in a blue frame',
  boxHeatmap: 'Box heatmap',
  boxHeatmapHint: 'The boxes in the annotation sets you may see, in the colours of their labels: deeper where boxes overlap.',
  overlayOff: 'No labels',
  overlayOffHint: 'Only the images',
  overlayCorners: 'Corners',
  overlayCornersHint: 'Age and projection in the lower corners',
  overlayCaptions: 'Captions',
  overlayCaptionsHint: 'A line along the bottom: body region, age, sex and site',
  galleryView: 'View',
  galleryViewHint: 'Grouping, box heatmap, labels on the images and their size',
  thumbnailSize: 'Thumbnail size',
  thumbnailSizeHint: 'Thumbnail size. Ctrl + mouse wheel over the images changes it too; a double click returns to the standard {size} px.',
  standardSize: 'standard size',
  keyCtrl: 'Ctrl',
  mouseWheel: 'wheel',
  keysMove: 'move',
  keysOpen: 'open',
  keySpace: 'Space',
  keysChoose: 'select',
  keysClear: 'clear the selection',
  mouseClick: 'click',
  mouseDoubleClick: 'double-click',
  mouseDrag: 'drag',
  galleryRange: '{from}–{to} of {total}',
  galleryOnScreen: '{count} rows, about {perRow} per row: {shown} on screen',
  galleryOnScreen_one: '1 row: {shown} on screen',
  searchCollections: 'Search by name or filter',
  anyBodyPart: 'Any body part',
  sortBy: 'Order',
  sortChanged: 'Last changed',
  sortName: 'Name',
  sortCreated: 'Newest',
  collectionCount: '{count} collections',
  collectionCount_one: '1 collection',
  collectionsShown: '{shown} of {total} collections',
  noCollectionsYet: 'No collections yet',
  noCollectionsMatch: 'No collection matches.',
  browseImages: 'Browse the images',
  myCollection: 'Mine',
  noFilters: 'No filters',
  changedOn: 'Changed {date}',
  collectionName: 'Name of the collection',
  revertChanges: 'Discard changes',
  saveAsNew: 'Save as new',
  collectionUses: 'Model runs and teaching',
  collectionTeaching: 'Teaching',
  usesSavedFilters: 'Runs and the teaching offer use the saved filters, not the unsaved changes.',
  startRun: 'Run on all images',
  run_pending: 'Queued',
  run_running: 'Running',
  run_done: 'Done',
  run_failed: 'Failed',
  run_cancelled: 'Cancelled',
  interactiveModelHint: 'Readers point at an object with clicks or a box and pick the polygon or mask label it becomes.',
  smartTool: 'Smart',
  smartHelp: 'Drag a box around the area in question; then click inside the object or Shift+click outside it to refine. Enter accepts the outline, Esc starts over.',
  smartNeedsLabel: 'Pick a polygon or mask class for the smart tool.',
  segmenting: 'Segmenting …',
  modelAnnotation: 'Annotation',
  modelAutomatic: 'Process every image automatically, as soon as it is uploaded, in the background',
  modelShownAtOnce: 'Show its suggestions as soon as an image opens, where no job keeps them on request',
  modelAnnotationIntro: 'Whoever may annotate a study may ask an enabled model for suggestions on it. Map each model class to a catalog label; unmapped classes are ignored.',
  enabled: 'Enabled',
  disabled: 'Off',
  mapClass: 'Label for',
  notUsed: 'Not used',
  enableModel: 'Enable',
  disableModel: 'Disable',
  suggestWith: 'Suggest with {model}',
  heatmapWith: 'Heatmap: {model}',
  heatmapShown: 'Heatmap for {finding} ({confidence}): red regions counted most.',
  hideHeatmap: 'Hide',
  raterHeatmap: 'Reader heatmap',
  raterHeatmapOff: 'Off',
  raterHeatmapHelp: 'Share of the {count} readers whose boxes cover each region: yellow few, red all.',
  raterHeatmapFailed: 'The reader heatmap could not be shown.',
  suggestionsHint: 'Suggestions appear only on request. Check each one; you remain responsible for what you accept.',
  reject: 'Reject',
  acceptAll: 'Accept all',
  minConfidence: 'Minimum confidence',
  rejectAll: 'Reject all',
  brushHelp: 'Drag to paint or erase; each stroke is saved. [ and ] change the size. Select a mask to add to it.',
  outlineHelp: 'Click around the area; a double click or Enter fills it.',
  finishAssignment: 'Finish assignment',
  assignmentUnavailable: '{count} images of this assignment are no longer available to you, so it cannot be finished yet. Please tell the job’s coordinator.',
  assignmentUnavailable_one: '1 image of this assignment is no longer available to you, so it cannot be finished yet. Please tell the job’s coordinator.',
  finishedReadOnly: 'This image is finished. Reopen it to change your annotations.',
  selectTool: 'Select',
  undo: 'Undo',
  redo: 'Redo',
  sidePanel: 'Side panel',
  findClass: 'Find a class',
  imageTags: 'Image tags',
  objects: 'Objects',
  noIdentifyingData: 'Do not enter identifying patient data.',
  save_loading: 'Loading…',
  save_saved: 'Saved',
  save_unsaved: 'Unsaved changes',
  save_saving: 'Saving…',
  save_retrying: 'Offline, retrying',
  save_conflict: 'Changed in another window',
  save_failed: 'Not saved',
  autosaveIdle: 'Saves automatically',
  autosaveEditing: 'Saves when you leave the field',
  loadStored: 'Load the stored version',

  agreement: 'Agreement',
  agreementIntro:
    'Over the first readings of both readers, kept when each completed their assignment, so later edits change nothing: kappa for image tags; F1 for finding shapes, and IoU and Dice of the marked regions for boxes and polygons.',
  agreementNeedsPairs: 'Agreement is computed once two readers have completed assignments with the same images.',
  label: 'Label',
  marked: 'Marked',
  bothReaders: 'Both',
  resolution: 'Resolution',
  majorityIntro:
    'Keeps findings marked by at least this many readers with the same label, drops findings at least this many readers did not mark, and leaves the rest disputed. Images resolved by an adjudicator stay unchanged.',
  minAgreeing: 'Minimum agreeing readers',
  maskMethod: 'Masks',
  maskMajority: 'Pixels at least half the readers marked',
  maskStaple: 'STAPLE: readers weighed by their estimated reliability',
  resolveMajority: 'Resolve by majority',
  majorityDone: '{images} images resolved, {disputed} findings disputed, {skipped} images skipped.',
  comparisonSettings: 'Comparison settings',
  matchIou: 'Same finding from overlap (IoU)',
  agreeIou: 'Same location from overlap (IoU)',
  matchDistance: 'Lines and points: same finding within (share of image diagonal)',
  agreeDistance: 'Lines and points: same location within (share of image diagonal)',
  numericTolerance: 'Tolerance for numeric attributes',
  readCount: 'Read',
  agreed: 'Agreed',
  discrepant: 'Discrepant',
  compare: 'Compare',
  notCompared: 'Compared once two readers have finished the image.',
  findings: 'Findings',
  showResolution: 'Show the resolution',
  missingBy: 'Not marked by {names}',
  experienceYears: '{years} years',
  resolution_majority: 'Majority',
  resolution_adjudication: 'Adjudicated',
  resolution_outdated: 'Outdated',
  reason_missed: 'missed',
  reason_label: 'label',
  reason_location: 'location',
  reason_attribute: 'attributes',
  finding_agreed: 'Agreed',
  finding_resolved: 'Resolved',
  finding_disputed: 'Disputed',
  present: 'present',
  absent: 'absent',
  adjudication: 'Adjudication',
  adjudicator: 'Adjudicator',
  jobRoles: 'Review and adjudication',
  jobRolesHint: 'Reviewers check completed assignments and return them; the adjudicator decides the discrepancies. Neither reads the job, and both must be able to open every study of it.',
  reviewers: 'Reviewers',
  nobody: 'Nobody',
  adjudicate: 'Adjudicate',
  adjudicatedImages: '{count} images adjudicated',
  adjudicatedImages_one: '1 image adjudicated',
  nothingToAdjudicate: 'There are no discrepancies to decide yet.',
  accept: 'Accept',
  notPresent: 'Not present',
  merge: 'Merge',
  markDisputed: 'Disputed',
  saveDecisions: 'Save decisions',
  evidence: 'Evidence',
  noEvidence: 'No later study cited',
  laterStudy: 'study {days} days later',
  openStudy: 'Open study',
  decidedOf: '{done} of {total} decided',
  datasets: 'Frozen collections',
  newDataset: 'Freeze studies',
  datasetIntro: 'A frozen collection fixes studies, the annotations chosen from a job and a split by patient. It never changes, and every export carries its checksum.',
  sourceJob: 'Job',
  annotationChoice: 'Annotations',
  choice_resolution: 'Resolution',
  choice_none: 'None, the studies alone',
  choice_checked: 'Sets of those who checked each study (no job)',
  frozenStudies: 'Studies',
  frozenStudiesOfJob: 'The job’s',
  noJob: 'None (no annotations)',
  choice_readers: "Readers' sets",
  choice_both: 'Resolution and readers’ sets',
  choice_first_readings: 'First readings',
  choice_resolution_first_readings: 'Resolution and first readings',
  firstReadingsHint: 'First readings are the sets each reader handed in when finishing their assignment, before any comparison; the readers’ sets are as they stand now.',
  disputedTreatment: 'Disputed findings',
  disputed_omit: 'Leave out',
  disputed_include: 'Include',
  disputed_ignore: 'Mark as regions to ignore',
  disputed_share: 'Label with the share of readers',
  splitRule: 'Patients go to training (70 %), validation or test (15 % each) by their split bin, the same in every frozen collection. Patients connected by equal pixels stay together.',
  splitConnected: '{count} patients connected by equal pixels had bins in different sets; their images went together to the set of their first frozen collection, or to training.',
  splitReview: 'Connected patients turned out to sit in different sets of this frozen collection. Review it, and the results of its benchmarks, before relying on them.',
  freeze: 'Freeze',
  splitCounts: '{train} / {validation} / {test} images',
  excludedImages: 'Excluded: {details}',
  excluded_outdated: '{count} outdated resolution',
  excluded_unresolved: '{count} not resolved',
  excluded_unread: '{count} not read',
  exportFormat: 'Format',
  format_coco: 'COCO JSON with 16-bit PNG',
  format_yolo: 'YOLO with 16-bit PNG',
  format_png_cvat: '16-bit PNG with CVAT 1.1 XML',
  format_cvat: 'CVAT 1.1 XML only',
  format_dicom: 'DICOM with the annotations in the header',
  leaves_coco: 'COCO leaves out points and polylines; the export’s manifest counts what it left out.',
  leaves_yolo: 'YOLO takes rectangles only and leaves out polygons, polylines, points, masks and image tags; the export’s manifest counts them.',
  exportAction: 'Export',
  evaluateModel: 'Evaluate a model',
  evaluationCurrentMap: 'This run predates recorded class maps, so its classes were read with the model’s map as it is now.',
  evaluationIntro: 'Compares the predictions of a finished model run over this frozen collection’s job with its annotations: per image and label sensitivity, specificity (95% Wilson intervals) and ROC-AUC; for areas also precision, recall and AP at IoU 0.5. Curves only see predictions above the run’s own threshold, so start the run with a low one.',
  evaluationNeedsRun: 'Evaluating needs a finished model run over this frozen collection’s job. Start one on the job’s page.',
  modelRun: 'Model run',
  reference: 'Reference',
  eachReader: 'Each reader',
  threshold: 'Threshold',
  evaluate: 'Evaluate',
  breakdown: 'Show',
  overall: 'Overall',
  resultsTooFew: 'No results: a result covers at least 20 images, and so does each group it is broken down into.',
  by_age_group: 'By age group',
  by_sex: 'By sex',
  by_body_part: 'By body region',
  by_site: 'By site',
  downloadJson: 'Download JSON',
  positivesNegatives: 'With / without',
  sensitivity: 'Sensitivity',
  specificity: 'Specificity',
  precision: 'Precision',
  recall: 'Recall',
  split_test: 'Test',
  split_validation: 'Validation',
  split_train: 'Training',
  split_all: 'All',
  reasonNoMajority: 'No majority',
  download: 'Download',
  readersCoded: 'Readers coded',
  readersCodedHint: 'Requested through a share: readers appear as reader-1, reader-2 and so on, not by their user ID.',
  export_pending: 'Queued',
  export_running: 'Building',
  export_done: 'Ready',
  export_failed: 'Failed',
  shortcuts: 'Keyboard shortcuts',
  shortcutsIntro: 'Select a key, then press the new one. A key used elsewhere moves here. Enter and Esc stay fixed.',
  shortcutGroup_editing: 'Drawing and editing',
  shortcutGroup_moving: 'Moving around',
  shortcutGroup_tools: 'Image and tools',
  shortcutGroup_labels: 'Labels and tags',
  shortcutKeysOf: '{action}: {keys}. Select to change.',
  shortcutNoKey: 'no key',
  shortcutClear: 'Clear the key of {action}',
  shortcutsReset: 'Every shortcut is back to its default.',
  shortcutsImported: 'The shortcuts from the file are in use.',
  shortcutsTouch: 'Set your shortcuts on a device with a keyboard; they follow your account.',
  change: 'Change',
  clear: 'Clear',
  pressKey: 'Press a key … (Esc cancels)',
  pressKeyShort: 'Press a key',
  shortcutMoved: '{key} moved here from {from}.',
  resetDefaults: 'Reset to defaults',
  exportShortcuts: 'Export',
  importShortcuts: 'Import',
  shortcutsInvalid: 'This file does not hold shortcuts.',
  command_draw: 'draw',
  command_select: 'select',
  command_join: 'Join masks',
  command_smart: 'smart tool',
  command_find: 'find a class',
  command_previous: 'previous study',
  command_earlierStudy: "patient's earlier study",
  command_laterStudy: "patient's later study",
  command_next: 'next study',
  command_otherView: 'other view',
  command_undo: 'undo',
  command_redo: 'redo',
  command_delete: 'delete',
  command_lock: 'lock or unlock the object',
  command_zoomIn: 'zoom in',
  command_zoomOut: 'zoom out',
  command_fit: 'fit',
  command_brushSmaller: 'smaller brush',
  command_brushLarger: 'larger brush',
  command_focus: 'focus mode',
  command_clahe: 'contrast enhancement (CLAHE)',
  command_edges: 'sharpening (unsharp mask, 200%)',
  command_crop: 'crop view',
  command_dictate: 'dictate (hold)',

  // The reading workspace, laid out like the standalone app (ANN-01 to ANN-15).
  close: 'Close',
  annotations: 'Annotations',
  imageInformation: 'Image information',
  categoryRectangles: 'Rectangles',
  categoryPolygons: 'Polygons',
  categoryPolylines: 'Polylines',
  categoryPoints: 'Points',
  categoryMasks: 'Masks',
  categoryTags: 'Image tags',
  categoryDrawHint: 'Choose a class, then draw in either image.',
  categoryTagHint: 'Click: sets or removes the tag on the highlighted image. Double click or key twice: on every image of the study.',
  keepOpen: 'Keep open',
  freeText: 'Free-text class',
  freeTextHelp: 'For a finding no class names: type a name for it. Do not enter identifying patient data.',
  freeTextField: 'Name a free-text class',
  freeTextNew: 'new free-text class',
  freeTextSuggestions: 'Classes and names in use',
  findClassHelp: 'Type a name or shape type. ↑/↓ to choose, Enter to use, Esc to close. A class starts drawing; an image tag is set on the active image.',
  clearSearch: 'Clear search',
  classCount: 'Matching classes: {count}',
  unassigned: 'Unassigned',
  noAnnotationsHere: 'No annotations on this image.',
  drawAnnotation: 'Draw an annotation',
  changeClass: 'Change class',
  editDetails: 'Edit details',
  allDetails: 'Details of all annotations',
  expandAllDetails: 'Expand all details',
  collapseAllDetails: 'Collapse all details',
  lockObject: 'Lock',
  lockHelp: 'Lock: the object keeps its place, class and qualifiers, and drawing beside it leaves it alone',
  unlockHelp: 'Locked: unlock it to change it',
  lockedByProfile: 'Locked by the labeling profile: choose another profile to change it',
  acceptedFrom: 'Accepted from {model} ({confidence})',
  tagOnAllViews: 'Set on all {count} views of the study',
  tagNotOnAllViews: 'Not saved on {count} of the views: open them to see why',
  notRated: 'Not rated',
  subtletyObvious: 'obvious',
  subtletyUnclear: 'unclear',
  subtletySubtle: 'subtle',
  firstSet: 'First view set',
  lastSet: 'Last view set',
  setNumber: 'Current view set number',
  setNavigation: 'View set navigation',
  viewsShown: 'Views shown at once',
  previousImage: 'Previous image of the study',
  nextImage: 'Next image of the study',
  readingBrand: 'READING',
  assignmentMenu: 'Assignment',
  imageMenu: 'Image',
  workspaceViewOnly: 'View only',
  assignmentMenuEyebrow: 'ASSIGNMENT',
  reviewEyebrow: 'REVIEW',
  comparisonEyebrow: 'COMPARISON',
  adjudicationEyebrow: 'ADJUDICATION',
  comparison: 'Comparison',
  datasetEyebrow: 'FROZEN COLLECTION',
  notInSet: 'not in this set',
  noImages: 'No images.',
  editShortcuts: 'Edit shortcuts',
  quickGuide: 'Quick guide',
  metricFractures: 'fractures',
  metricImages: 'images',
  metricViewSets: 'view sets',
  viewFilter: 'View filter',
  aiEyebrow: 'ASSISTED LABELING',
  aiReady: 'Ready',
  aiRunning: 'Running',
  aiCount: '{count} found',
  aiRunHelp: 'Ask the model for suggestions on every view of this study. Nothing is accepted automatically.',
  duplicateDatabase: 'Duplicate database',
  dbEyebrow: 'DUPLICATES',
  archiveEvidence: 'Archive evidence',
  dbChecking: 'Checking',
  dbClear: 'Clear',
  dbFound: '{count} found',
  dbOffline: 'Offline',
  dbNone: 'No copies or similar images of this image elsewhere on the platform.',
  readerEyebrow: 'READER',
  annotationTools: 'Annotation tools',
  resetDisplay: 'Reset image display',
  exitFocus: 'Exit focus',
  finishContour: 'Finish contour',
  activeView: 'ACTIVE VIEW',
  activeViewHelp: 'Tools and the annotation list apply to the highlighted view.',
  demographicsUnknown: 'Age and sex unavailable',
  guideDrawTitle: 'Choose a class and draw',
  guideDraw: 'Hover a category in the left rail, use a class key or Find a class (C). Rectangle: click the first corner, then the opposite one (or drag). Contours: click vertices, then double-click or press Enter. Esc cancels.',
  guideQualifiersTitle: 'Set qualifiers',
  guideQualifiers: 'Hover or select a fracture box: its age is the slider above it, its subtlety (0 obvious, 1 subtle) the slider below. The arrow on a row in the list opens all its details, the arrow above the list those of every row; its padlock (L) locks the object, so drawing beside it never moves or changes it.',
  guideViewsTitle: 'Read both views',
  guideViews: 'Up to two views of a study are side by side; the toggle at the top shows one or two, and the arrows slide through a longer study. Q switches the highlighted view; the tools and the list apply to it. While drawing, the first click in the other view starts there. The filmstrip above the views holds the patient’s studies, oldest first: Shift+← and Shift+→ open the earlier and later one for annotation, and D and F go on from the study you came from.',
  guideAdjustTitle: 'Adjust the radiograph',
  guideAdjust: 'Drag to pan; drag up at an image edge to zoom in, down to zoom out; the wheel zooms too. Space + drag pans anywhere. Middle- or right-drag sets window/level; W/L saves it for the image.',
  guideFinishTitle: 'Finish the image',
  guideFinish: 'Changes save automatically. When an image is read, finish it in the side panel; the next open image follows.',
  command_shortcuts: 'edit shortcuts',

  role_site_admin: 'Site admin',
  role_coordinator: 'Coordinator',
  role_grantee: 'Grant recipient',
  role_member: 'Member',
  role_platform_admin: 'Platform admin',
  role_catalog_editor: 'Catalog editor',
  role_educator: 'Educator',
  role_learner: 'Learner',
  role_reader: 'Reader',
  role_adjudicator: 'Adjudicator',
  role_reviewer: 'Reviewer',

  // Image protection (PRT-01, PRT-03, PRT-10).
  markNotice: 'The images you see carry an invisible mark that identifies your account, so copies can be traced. Never copy, photograph or pass them on.',
  markNoticeOk: 'Understood',
  imageRenderFailed: 'The image could not be shown.',
  errorReference: 'Error reference',
  errorDetails: 'Technical details',
  pageFailed: 'The page could not be displayed.',
  reloadPage: 'Reload page',
  errorReports: 'Error reports',
  loadErrorReports: 'Load recent errors',
  noErrorReports: 'No errors found.',
  retry: 'Try again',
  ...educationEn,
  ...collectionsEn,
  ...catalogEn,
  ...labelingEn,
  ...queryEn,
  ...quickTagEn,
  ...checksEn,
  ...impactEn,
  ...studyTagsEn,
  ...inferenceEn,
  ...benchmarksEn,
  ...contributionsEn,
  ...uploadsEn,
  ...dictationEn,
  ...cloudEn,
  ...forumEn,
  ...schedulingEn,
  ...peopleEn,
  ...sitesEn,
  ...maskJoinEn,
}

export type MessageKey = keyof typeof en

const de: Record<MessageKey, string> = {
  researchOnly: 'Nur für Forschungszwecke — nicht zur Diagnose',
  appTagline: 'Frakturforschung bei Kindern',
  mainNavigation: 'Hauptnavigation',
  menu: 'Menü',
  navWork: 'Arbeit',
  navManage: 'Verwaltung',
  navPlatform: 'Plattform',
  language: 'Sprache',
  version: 'Version',
  unread: 'ungelesen',
  loading: 'Wird geladen …',
  save: 'Speichern',
  cancel: 'Abbrechen',
  confirm: 'Bestätigen',
  remove: 'Entfernen',
  add: 'Hinzufügen',
  create: 'Anlegen',
  logout: 'Abmelden',
  profile: 'Profil',
  sites: 'Standorte',
  notFoundTitle: 'Seite nicht gefunden',
  manualContents: 'Inhalt',
  notFound: 'Diese Seite existiert nicht, oder Sie dürfen sie nicht öffnen.',
  name: 'Name',
  email: 'E-Mail',
  role: 'Rolle',
  none: 'Noch keine.',
  copy: 'Kopieren',
  copied: 'Kopiert',
  copyFailed: 'Nicht kopiert: markieren und kopieren',
  loginFailed: 'Die Anmeldung konnte nicht abgeschlossen werden. Bitte erneut versuchen.',
  accountInactive: 'Ihr Konto ist deaktiviert. Bitte wenden Sie sich an Ihre Standort-Administration.',
  loginAgain: 'Anmelden',
  sessionEnded: 'Ihre Sitzung ist abgelaufen. Melden Sie sich erneut an, um fortzufahren.',

  experienceNote:
    'Wird mit jedem Annotationssatz gespeichert, damit die Erfahrung der Befundenden ausgewertet werden kann. Sichtbar für Sie und wer Ihre Aufträge oder Kurse leitet; für andere nur zusammengefasst.',
  experienceRequired: 'Vor dem Start nötig',
  experienceContinue: 'Speichern und weiter',
  specialty: 'Fachgebiet',
  chooseSpecialty: 'Fachgebiet wählen',
  readingSince: 'Befundung von Traumaröntgenbildern seit',
  accountSecurity: 'Name, Passwort und Anmeldung',

  description: 'Beschreibung',
  ethicsReference: 'Ethikvotum (Referenz)',
  ethicsValidUntil: 'Ethikvotum gültig bis',
  site: 'Standort',
  unknownSite: 'Anderer Standort',
  members: 'Mitglieder',
  searchUser: 'Nach Name, Benutzername oder E-Mail suchen (mindestens 3 Zeichen)',
  details: 'Details',
  pageSections: 'Bereiche dieser Seite',
  tabAgreement: 'AV-Vertrag',
  tabAccess: 'Anmeldung und Support',

  newSite: 'Neuer Standort',
  siteCode: 'Kürzel (Kleinbuchstaben, Ziffern, Bindestriche)',
  siteName: 'Standortname',
  invite: 'Per E-Mail einladen',
  firstName: 'Vorname',
  lastName: 'Nachname',
  invitationSent: 'Einladung gesendet.',

  // Der Arbeitsbereich der Standorte: die Standorte des Nutzers neben der Token-Prüfung und die Bereiche des offenen Standorts.
  people: 'Personen',
  canUpload: 'Darf hochladen',
  peopleCanUpload: '{enrolled} von {total} dürfen hochladen',
  membersCount: '{count} Mitglieder',
  membersCount_one: '1 Mitglied',
  ready: 'Bereit',
  dpaNoEnd: 'ohne Enddatum',
  storageNotActive: '{count} Speicherorte nicht aktiv',
  storageNotActive_one: '1 Speicherort nicht aktiv',
  sharesWaiting: '{count} warten auf Zustimmung',
  sharesWaiting_one: '1 wartet auf Zustimmung',
  loginConnected: 'Anmeldung über den Identitätsanbieter des Standorts verbunden',
  ssoConnected: 'Verbunden',
  state_ok: 'in Ordnung',
  state_warn: 'braucht Aufmerksamkeit',
  yourAccess: 'Ihr Zugang',
  uploadToken: 'Upload-Token',
  enrolledForSite: 'Sie sind für das Token dieses Standorts berechtigt. Prüfen Sie das Token, das Sie erhalten haben, mit der Token-Prüfung.',
  notEnrolledForSite: 'Sie sind für das Token dieses Standorts noch nicht berechtigt. Bitten Sie einen Standort-Admin von {site}, Sie zu berechtigen.',
  platformView: 'Ansicht als Plattform-Admin',
  yourToken: 'Ihr Token',
  waitingCount: '{count} wartend',
  uploadsBlocked: 'Uploads gesperrt',
  secondFactorRules: 'Regeln für den zweiten Faktor',
  storageLocations: 'Speicherorte',
  storageDefault: 'Vorgabe für neue Uploads',
  storageProcessing: 'Verarbeitung auf der Plattform',
  storageProcessingHint: 'Erlaubt den Servern der Plattform, Ansichten und Vorschaubilder der Bilder hier darzustellen, ihre Duplikat-Fingerabdrücke zu berechnen und Modelle auf ihnen laufen zu lassen. Ohne sie sind diese Funktionen für diese Bilder nicht verfügbar; Uploads werden weiter geprüft und Dateien erreichen berechtigte Nutzer weiterhin.',
  storageHostedHint: 'Von der Plattform bereitgestellt, immer verfügbar',
  storageAlwaysOn: 'Immer aktiv',
  storageUsePerLocation: 'Nutzung je Speicherort',
  storageUsePerCollection: 'Nutzung je Sammlung des Standorts',
  storageMoves: 'Bilder verschieben',
  storageMovesHint:
    'Verschiebt die Bilder des Standorts an einen anderen seiner Speicherorte: alle oder die, die eine Sammlung jetzt enthält. Sie werden einzeln im Hintergrund verschoben und behalten ihre Annotationssätze, Erstbefundungen und Auflösungen. Es läuft jeweils eine Verschiebung.',
  storageMoveWhat: 'Bilder',
  storageMoveAll: 'Alle Bilder des Standorts',
  storageMoveSiteCollections: 'Sammlungen des Standorts',
  storageMoveOwnCollections: 'Ihre Sammlungen',
  storageMoveCollection: '{name} ({count} Bilder)',
  storageMoveCollection_one: '{name} (1 Bild)',
  storageMoveTo: 'Verschieben nach',
  storageMoveTarget: 'Nach',
  storageMove: 'Verschieben',
  storageMoveConfirm: 'Nach {target} verschieben: {what}?',
  storageMoveProgress: 'Bisher verschoben: {what}',
  storageMoveProgressHead: 'Verschoben',
  storageMoveStarted: 'Gestartet',
  storageMoveUnseen: 'Eine Sammlung',
  move_pending: 'Wartet',
  move_running: 'Läuft',
  move_done: 'Fertig',
  move_failed: 'Fehlgeschlagen',
  move_cancelled: 'Abgebrochen',
  storageLocation: 'Speicherort',
  sharingPolicyHint: 'Was der Standort erlaubt, wenn seine Daten ihn verlassen.',
  outsideAccessHint: 'Wer von außerhalb des Standorts seine Sammlungen, Aufträge oder eingefrorenen Sammlungen geöffnet hat.',

  tokenFromGenerator:
    'Die Schlüsselverwaltung erzeugt das Token des Standorts mit dem Schlüsselgenerator und gibt Ihnen das Wiederherstellungsblatt. Geben Sie das Token und das Zertifikat vom Blatt ein. Gesendet werden nur die Schlüssel-ID und das Zertifikat.',
  tokenLostWarning:
    'Ein verlorenes Token kann nicht wiederhergestellt werden. Bewahren Sie das Wiederherstellungsblatt verschlossen am Standort auf. Ein neues Token gibt es nur von der Schlüsselverwaltung, und es gibt wiederkehrenden Patientinnen und Patienten neue Pseudonyme.',
  keyCertificate: 'Zertifikat des Schlüsselgenerators',
  certificateInvalid: 'Das ist kein Zertifikat des Schlüsselgenerators.',
  certificateOtherToken: 'Dieses Zertifikat gehört zu einem anderen Token.',
  keyGeneratedNo: 'Token Nr. {serial}',
  keyAcceptedNo: 'Token Nr. {serial}, früher erzeugt und übernommen',
  token: 'Token (64 Hexadezimalzeichen)',
  tokenInvalid: 'Ein Token besteht aus genau 64 Hexadezimalzeichen.',
  alreadyAnonymized: 'Bereits anonymisiert',
  filesOfOtherSite: 'Einige Dateien wurden bereits mit dem Token eines anderen Standorts anonymisiert oder mit einem, für das Sie nicht berechtigt sind. Die Plattform lehnt sie in diesem Upload ab.',
  keyLabel: 'Bezeichnung',
  keyId: 'Schlüssel-ID',
  registerKey: 'Schlüssel-ID registrieren',
  retireKey: 'Token stilllegen',
  retireWarning: 'Mit diesem Token werden keine Uploads mehr angenommen.',

  anonymizationToken: 'Anonymisierungstoken',
  tokenPanelIntro: 'Uploads werden mit dem Token Ihres Standorts anonymisiert. Es wird in diesem Browser geprüft und nirgendwohin gesendet; die Plattform erfährt nur seine Schlüssel-ID.',
  tokenSet: 'Token gesetzt',
  tokenNotSet: 'Kein Token gesetzt',
  tokenNotRecognized: 'Token nicht erkannt',
  tokenChecking: 'Token wird geprüft…',
  tokenLocked: 'Mit diesem Token werden gerade Dateien geprüft oder hochgeladen. Ändern Sie es, wenn sie fertig oder angehalten sind.',
  forgetToken: 'Token vergessen',
  setToken: 'Token setzen',
  uploadNeedsToken: 'Setzen Sie das Token Ihres Standorts, um Dateien zu prüfen und hochzuladen.',
  tokenMatches: 'Dieses Token gehört zu {site}. Sie sind für Uploads damit berechtigt.',
  tokenNoMatch: 'Dieses Token passt zu keinem Token, für das Sie berechtigt sind. Bitte auf Tippfehler prüfen.',

  images: 'Bilder',
  any: 'Alle',
  bodyPart: 'Körperregion',
  laterality: 'Seite',
  view: 'Projektion',
  sex: 'Geschlecht',
  age: 'Alter (Jahre)',
  minAge: 'Mindestalter (Jahre)',
  maxAge: 'Höchstalter (Jahre)',
  left: 'Links',
  right: 'Rechts',
  both: 'Beidseits',
  female: 'Weiblich',
  male: 'Männlich',
  otherSex: 'Divers',
  pseudonym: 'Patientenpseudonym',
  shiftedDate: 'Verschobenes Datum',
  imageCount: '{count} Bilder',
  imageCount_one: '1 Bild',
  previous: 'Zurück',
  next: 'Weiter',
  loadingImage: 'Bild wird geladen …',
  dimensions: 'Größe',
  sameImageIn: 'Dasselbe Bild ist noch {count}-mal gespeichert',
  sameImageIn_one: 'Dasselbe Bild ist noch einmal gespeichert',
  similarImages: '{count} ähnliche Bilder an anderer Stelle',
  similarImages_one: 'Ein ähnliches Bild an anderer Stelle',
  similarEvidence: 'Übereinstimmung {agreement}, Abdeckung {coverage}',
  similarInverted: 'Grauwerte umgekehrt',
  similarFiles: 'Dateien, die Bildern auf der Plattform ähneln ({count})',
  similarIsEvidence: 'Vom OXF-RI-Fingerabdruck gefunden. Das ist ein Hinweis, kein Urteil: Vergleichen Sie die Bilder, bevor Sie danach handeln.',
  fit: 'Einpassen',
  invert: 'Invertieren',
  clahe: 'CLAHE',
  edges: 'Schärfen 200 %',
  claheHelp: 'Kontrastanhebung (CLAHE): hebt den Kontrast Bereich für Bereich an. Nur für die Anzeige, wird nie mit dem Bild gespeichert.',
  edgesHelp: 'Unscharfmaskierung mit 200 %: schärft feine Details wie Kortikalis und Frakturlinien, auch das Rauschen, und kann helle oder dunkle Säume an Kanten zeichnen. Nur für die Anzeige, wird nie mit dem Bild gespeichert.',
  crop: 'Zuschnitt',
  cropHelp: 'Zuschnittansicht: Bilder mit einem Zuschnitt-Rechteck (crop) zeigen nur, was es umschließt, etwa das Bild ohne seinen Rand. Nur für die Anzeige; das Bild bleibt ganz.',
  enhancedHelp: 'Nur für die Anzeige verändert. Das Bild selbst bleibt unverändert.',
  resetWindow: 'Fenster zurücksetzen',
  saveWindow: 'Fenster speichern',
  saveWindowHelp: 'Öffnet dieses Bild für alle mit der aktuellen Fensterung und hält sie bei den Annotationen fest. Andere DICOM-Viewer behalten die Fensterung der Datei.',
  windowSaved: 'Gespeicherte Fensterung',
  windowSavedHelp: 'Dieses Bild öffnet sich mit seiner gespeicherten Fensterung.',
  adjustWindowBeforeSaving: 'Passen Sie die Fensterung vor dem Speichern an. Bis dahin bleibt die ursprüngliche Darstellungskurve erhalten.',
  imageViewport: 'Bildansicht',
  confirmPersonalAuthorized: 'Ich bin berechtigt, diese Bilder auf die Plattform hochzuladen und dort zu speichern.',
  selectedStudies: 'Ausgewählte Studien',
  studiesSelected: '{count} Studien ausgewählt',
  studiesSelected_one: '1 Studie ausgewählt',
  chooseStudy: 'Studie von {image} auswählen',
  privateStudy: 'privat',
  keepStudyPrivate: 'Privat halten',
  shareStudiesWithSite: 'Mit Standort teilen',
  keepStudyPrivateHint: 'Eine private Studie können nur Sie öffnen. Sonst können die anderen Mitglieder Ihres Standorts sie öffnen und annotieren.',
  deleteStudies: '{count} Studien löschen',
  deleteStudies_one: '1 Studie löschen',
  deleteStudiesWarning: 'Alle Bilder dieser Studien werden mit ihren Annotationen gelöscht.',
  clearSelection: 'Auswahl aufheben',
  tooManyStudies: 'Wählen Sie höchstens {count} Studien auf einmal aus.',
  notEnrolled:
    'Sie sind für kein Standort-Token berechtigt. Bitten Sie Ihre Standort-Administration, Sie vor dem Hochladen zu berechtigen.',
  chooseFiles: 'DICOM-Dateien oder ZIP-Archive wählen',
  chooseFolder: 'Ordner wählen',
  chooseImage: 'Bild wählen',
  dropHint: 'Sie können Dateien und Ordner auch hierher ziehen.',
  rememberToken: 'Token {days} Tage in diesem Browser merken. Wer dieses Browserprofil nutzt, kann es dann verwenden.',
  pauseUpload: 'Anhalten',
  resumeUpload: 'Fortsetzen: die übrigen {count} Dateien senden',
  resumeUpload_one: 'Fortsetzen: die übrige Datei senden',
  reviewTitle: 'Bilder vor dem Hochladen',
  reviewSummary: '{count} Dateien bereit',
  reviewSummary_one: '1 Datei bereit',
  reviewBurnedIn: '{count} geben eingebrannte Beschriftung an, meist eine Seitenmarkierung',
  reviewBurnedIn_one: '1 gibt eingebrannte Beschriftung an, meist eine Seitenmarkierung',
  reviewBurnedInBadge: 'Eingebrannte Beschriftung',
  reviewIssuers: 'Die Dateien nennen verschiedene Aussteller der Patienten-ID',
  reviewInstitutions: 'Die Dateien nennen verschiedene Einrichtungen',
  reviewOriginsIntro: 'Bilder aus einem anderen Krankenhaus, deren Patienten-IDs Ihr Archiv durch die des Standorts ersetzt hat, gehören zu den Patienten des Standorts und bleiben. Lassen Sie Dateien weg, die noch die Patienten-IDs eines anderen Krankenhauses tragen.',
  reviewNoneNamed: 'Keine angegeben',
  reviewGroupFiles: '{count} Dateien',
  reviewGroupFiles_one: '1 Datei',
  reviewLeaveOutGroup: 'Diese weglassen',
  reviewIdentity: 'Patienten-IDs zum Prüfen',
  reviewIdentityIntro: 'Diese Dateien werden gesendet, wenn Sie sie nicht weglassen, denn sie können stimmen. Eine Patienten-ID ohne ihre führenden Nullen gibt einem Kind ein zweites Pseudonym: Korrigieren Sie den Export des Archivs oder lassen Sie die Dateien weg.',
  reviewLeadingZeros: 'Patienten-IDs, die sich nur in führenden Nullen unterscheiden',
  reviewRecordsDiffer: 'Eine Patienten-ID mit verschiedenen Geburtsdaten oder Namen',
  identityLeaveOutIntro: 'Oder lassen Sie die markierten Dateien weg: Die übrigen werden ohne sie erneut geprüft.',
  identityLeaveOut: '{count} Dateien weglassen',
  identityLeaveOut_one: '1 Datei weglassen',
  reviewShow: 'Bilder zeigen',
  reviewHide: 'Bilder ausblenden',
  reviewRange: '{from}–{to} von {count}',
  reviewIntro: 'Jedes Bild wird so gezeigt, wie es hochgeladen wird, mit dem Text, den die Datei trägt; Bilder mit eingebrannter Beschriftung kommen zuerst. Ein Klick auf ein Bild lässt die Datei aus, ein weiterer nimmt sie wieder auf.',
  reviewPreparing: 'Diese Dateien werden zur Ansicht anonymisiert…',
  reviewToggle: '{name} hochladen',
  reviewFullSize: 'Volle Größe',
  reviewTexts: 'Text in der Datei',
  reviewDetails: 'Pseudonym und Dateiname',
  why_review: 'Beim Durchsehen der Bilder vor dem Hochladen ausgelassen: Klicken Sie dort auf ihr Bild, um sie wieder aufzunehmen.',
  outputName: 'Dateiname',
  correctionsIntro: 'Was Sie hier eingeben, bleibt in diesem Browser und dient nur zum Anonymisieren der gewählten Dateien. Leere Felder ändern nichts.',
  correctionsApply: 'Übernehmen und erneut prüfen',
  engineDownloading: 'Die Anonymisierung wird geladen (etwa 20 MB, nur beim ersten Mal) …',
  engineStarting: 'Die Anonymisierung wird gestartet …',
  studies: 'Studien',
  accepted: 'Angenommen',
  duplicates: 'Bereits hochgeladen',
  rejected: 'Abgelehnt',
  inProgress: 'In Bearbeitung',
  confirmNoBurnedInText: 'Ich habe die Bilder geprüft, soweit ich es für nötig hielt: Sie enthalten keinen eingebrannten identifizierenden Text.',
  burnedInLeaveOut: 'Die {count} Dateien mit eingebrannter Beschriftung weglassen',
  burnedInLeaveOut_one: 'Die Datei mit eingebrannter Beschriftung weglassen',
  anonymizeAndUpload: '{count} Dateien anonymisieren und hochladen',
  anonymizeAndUpload_one: '1 Datei anonymisieren und hochladen',
  file: 'Datei',
  status: 'Status',
  reason: 'Grund',
  why_not_dicom: 'Keine DICOM-Datei. Hochladen lassen sich nur DICOM-Dateien.',
  why_unreadable: 'Die Datei lässt sich nicht lesen: Sie wurde nach der Auswahl verschoben oder geändert, oder das Archiv ist beschädigt oder verschlüsselt.',
  why_burned_in: 'Ausgelassen: Laut Datei enthält das Bild eingebrannte Beschriftungen.',
  why_unsupported: 'Dieser Bildtyp wird nicht unterstützt: nur einzelne Graustufen-Röntgenbilder (CR, DX).',
  why_transfer: 'Die Datei ist nicht vollständig angekommen. Senden Sie sie erneut.',
  why_damaged: 'Die Bilddaten sind beschädigt oder wurden unterwegs verändert.',
  why_identity: 'Die Patienten-ID fehlt oder ist ungültig, daher lässt sich kein Pseudonym bilden.',
  why_patient_id_placeholder: 'Die Patienten-ID wiederholt nur ein Zeichen, wie Platzhalter für unbekannte Patienten, und würde alle unbekannten Patienten zu einem verbinden. Die Datei wird nie geändert: Geben Sie die Patienten-ID ein, die der Standort diesem Patienten gab, oder lassen Sie die Datei weg.',
  why_identity_uids: 'Dieselbe Studie, Serie oder dasselbe Bild steht unter verschiedenen Patienten-IDs, daher ist unklar, zu wem es gehört. Korrigieren Sie die Patienten-ID der Dateien mit der falschen ID oder lassen Sie sie weg; die übrigen werden dann erneut geprüft.',
  why_identity_records: 'Die Dateien einer Patienten-ID nennen verschiedene Patienten: Geburtsdaten und Namen unterscheiden sich beide. Die ID ist vielleicht ein Platzhalter oder doppelt vergeben. Korrigieren Sie die Patienten-ID der Dateien, die zu einem anderen Patienten gehören, oder lassen Sie sie weg.',
  why_site_setup: 'Der Standort hat seine Zeitzone noch nicht festgelegt. Die Standort-Administration legt sie auf der Seite des Standorts neben dem Standort-Token fest.',
  why_token_required: 'Diese Datei muss noch anonymisiert werden: Geben Sie das Token des Standorts ein.',
  why_key_not_enrolled: 'Die Datei wurde mit einem Token anonymisiert, für das Sie nicht freigeschaltet sind.',
  why_key_other_site: 'Die Datei wurde mit dem Token eines anderen Standorts anonymisiert als dem dieses Uploads.',
  why_reference_unresolved: 'Die Datei verweist auf Bilder, die dieser Upload nicht enthält, so wie ein zusammengesetztes Bild auf die Einzelaufnahmen, aus denen es entstand und die meist nicht archiviert werden. Sie wurde nicht hochgeladen. Laden Sie sie ohne diese Verweise hoch.',
  omitReferencesIntro: 'Verweise auf Bilder, die nicht in diesem Upload sind, werden aus den anonymisierten Dateien entfernt; Verweise auf Bilder in ihm bleiben, und die Bilder selbst bleiben unverändert.',
  omitReferences: 'Ohne diese Verweise hochladen',
  showImage: 'Bild anzeigen',
  imagePreparing: 'Wird vorbereitet…',
  imageCannotShow: 'Nicht darstellbar',
  why_reference: 'Ein Verweis auf ein anderes DICOM-Objekt lässt sich nicht sicher anonymisieren. Die Datei wurde nicht hochgeladen. Die referenzierten Objekte oder eine geprüfte Entscheidung zum Weglassen optionaler Verweise werden benötigt.',
  uploadHistoryHint: 'Diese Zahlen zählen die an die Plattform gesendeten Dateien. Vor dem Senden zurückgehaltene Dateien stehen bei ihrem Upload unter Uploads, solange dieser Tab offen bleibt.',
  why_agreement: 'Für den Standort, der die Datei anonymisiert hat, gilt kein gültiger Auftragsverarbeitungsvertrag.',
  why_foreign:
    'Anderswo anonymisiert, etwa mit der Standalone-App oder einer früheren Version. Laden Sie die Originale hoch oder die Dateien, die ein Importer im Format der Plattform liefert.',
  why_record: 'Der Anonymisierungsnachweis der Datei fehlt oder ist ungültig. Anonymisieren Sie die Originaldatei erneut.',
  why_unsafe_metadata: 'Die Datei enthält unerwartete Metadaten, die einen Patienten identifizieren könnten. Anonymisieren Sie die Originaldatei erneut.',
  why_retained_characteristic: 'Die Datei enthält Patientenmerkmale, die das Anonymisierungsprofil nicht sicher beibehalten kann. Lassen Sie sie aus und kontaktieren Sie Ihre Administration.',
  why_presentation: 'Das Bild nennt eine Darstellungskurve (VOI LUT Function), die die Plattform nicht kennt. Lassen Sie es aus oder exportieren Sie es an der Modalität mit einem linearen Fenster.',
  why_foreign_labels: 'Die in der Datei enthaltenen Annotationen verwenden Labels, die der Katalog nicht hat. Ordnen Sie sie Katalog-Labels zu.',
  why_annotations: 'Die in der Datei enthaltenen Annotationen sind ungültig.',
  why_conflict: 'Die Metadaten der Datei widersprechen sich, etwa bei Körperregion, Seite, Ansicht oder Datum.',
  why_missing_value: 'Ein nötiger Wert wie Körperregion, Seite, Ansicht oder Aufnahmedatum fehlt oder wird nicht erkannt.',
  why_invalid_value: 'Ein Wert in der Datei ist ungültig, etwa das Alter, die Ansicht oder die Fensterung.',
  why_validation_failed:
    'Die Prüfung dieser Datei ist auf dem Server wiederholt fehlgeschlagen. Bitte informieren Sie die Administration der Plattform.',
  why_worker: 'Die Anonymisierung im Browser ist fehlgeschlagen, etwa aus Speichermangel. Prüfen Sie die Dateien erneut.',
  why_interrupted: 'Unterbrochen. Prüfen Sie die Dateien erneut, um den Rest zu senden.',
  why_other: 'Die Datei lässt sich so nicht verwenden.',

  reading: 'Befundung',
  myAssignments: 'Meine Befundungspakete',
  myContributions: 'Meine Beiträge · {site}',
  noAssignments: 'Noch keine Befundungspakete',
  noOpenAssignments: 'Alle Ihre Befundungspakete sind abgeschlossen',
  noAssignmentsHint: 'Wenn eine Koordinatorin oder ein Koordinator Ihnen Studien zur Befundung gibt, erscheint das Paket hier mit seinem Fortschritt, und Sie öffnen es von hier. Bis dahin können Sie jedes Bild öffnen, das Sie sehen dürfen, und es in Ihrem eigenen Satz annotieren.',
  readingAside: 'Zur Befundung',
  yourContributions: 'Ihre Beiträge',
  yourContributionsHint: 'Ihr Platz unter den Befundenden jedes Standorts, nach den Studien, die Sie abgeschlossen haben.',
  readingHow: 'So läuft die Befundung',
  readingHowAssignments: 'Ein Paket enthält die Studien, die Ihnen eine Koordinatorin oder ein Koordinator gegeben hat, mit den Labels und Anweisungen dazu.',
  readingHowFinish: 'Ihre Annotationen werden laufend gespeichert; markieren Sie jedes Bild als abgeschlossen, wenn Sie damit fertig sind.',
  readingHowCompared: 'Abgeschlossene Befundungen werden mit denen der anderen Befundenden verglichen, und ihre Unterschiede werden aufgelöst.',
  finishedAssignments: 'Abgeschlossene Pakete ({count})',
  job: 'Auftrag',
  jobs: 'Aufträge',
  newJob: 'Neuer Auftrag',
  jobName: 'Name des Auftrags',
  readersPerImage: 'Befundende pro Bild',
  assignmentSize: 'Bilder pro Paket',
  readers: 'Befundende',
  noReaders: 'Fügen Sie zuerst Mitglieder mit der Rolle Eigentümer:in, Manager:in oder Annotator:in hinzu.',
  selection: 'Bildauswahl',
  selectionHint: 'Alle Bilder, die diesen Filtern entsprechen, werden aufgenommen; eine Studie wird nie auf mehrere Pakete verteilt.',
  fromCollection: 'Aus einer Sammlung',
  conditions: 'Annotationen und Befundung',
  condition: 'Bedingung',
  addCondition: 'Bedingung hinzufügen',
  conditionPresence: 'Mit oder ohne',
  conditionWith: 'Mit',
  conditionWithout: 'Ohne',
  conditionKind: 'Was',
  conditionLabel: 'Label',
  conditionAnyLabel: 'Annotation',
  conditionAssigned: 'Befundungspaket',
  conditionFinished: 'Befundungsabschluss',
  conditionResolved: 'Konsens',
  conditionBy: 'Von wem',
  byMe: 'von mir',
  byAnyone: 'von irgendwem',
  byAnyoneHint: '„Von irgendwem“ zählt die Annotationen, Befundungen und Konsense aller, deren Arbeit Sie sehen dürfen: an den Studien Ihrer Standorte und den Ihnen freigegebenen, nie an Bildern, die Sie verblindet befunden.',
  unpaired: 'Unpaarig',
  collections: 'Sammlungen',
  newCollection: 'Neue Sammlung',
  saveCollection: 'Sammlung speichern',
  deleteCollection: 'Sammlung löschen',
  collectionsIntro: 'Eine Sammlung enthält Studien, die Sie öffnen dürfen: die, die jetzt zu ihren Filtern passen, die, die Sie hineinlegen, oder, eingefroren, die, die sie beim Einfrieren enthielt. Sie kopiert nie ein Bild und gibt niemandem Zugriff auf irgendetwas.',
  createJob: 'Auftrag erstellen',
  created: 'Erstellt',
  progress: 'Fortschritt',
  finishedOf: '{finished} / {total}',
  openJob: 'Öffnen',
  job_new: 'Neu',
  job_in_progress: 'In Bearbeitung',
  job_finished: 'Abgeschlossen',
  job_returned: 'Zurückgegeben',
  reading_finished: 'Abgeschlossen',
  readingSummary: '{finished} abgeschlossen · {progress} in Bearbeitung · {open} offen',
  histogram: 'Die Pixelwerte des Bildes, darüber das eingestellte Fenster',
  atDetector: 'am Detektor',
  viewN: 'Ansicht {index}',
  filterFrom: '{name} ab {value}',
  maskTool: 'Maskenwerkzeug',
  brush_paint: 'Malen',
  brush_erase: 'Radieren',
  brush_outline: 'Umranden',
  brushSize: 'Pinselgröße',
  newMask: 'Neue Maske',
  aiSuggestions: 'KI-Vorschläge',
  aiModels: 'KI-Modelle',
  registerModel: 'Modellversion registrieren',
  modelService: 'Modelldienst',
  modelKey: 'Modelldatei im Dienst',
  loadFromService: 'Art und Klassen vom Dienst laden',
  modelVersion: 'Version',
  modelKind: 'Art',
  modelClasses: 'Klassen (durch Komma getrennt)',
  inputWindow: 'Eingabefenster',
  window_percentile: 'Perzentile (0,5–99,5 %)',
  window_dicom: 'DICOM-Fenster',
  window_minmax: 'Voller Wertebereich',
  maxInputSize: 'Längste Eingabeseite in Pixeln (0: original)',
  intendedUse: 'Zweckbestimmung',
  license: 'Lizenz',
  kind_detection: 'Detektion',
  kind_segmentation: 'Segmentierung',
  kind_classification: 'Klassifikation',
  kind_interactive: 'Interaktive Segmentierung',
  draft_restored: 'Ungespeicherte Änderungen der letzten Sitzung wurden wiederhergestellt.',
  focusMode: 'Fokus',
  reviews: 'Überprüfungen',
  notifications: 'Benachrichtigungen',
  markAllRead: 'Alle als gelesen markieren',
  emailNotifications: 'Auch per E-Mail',
  emailNotificationsIntro: 'E-Mails sind Sicherheitsmeldungen und Benachrichtigungen mit Handlungsbedarf vorbehalten. Upload-Ergebnisse und behobene Plattformprobleme erscheinen nur hier. Sicherheitsmeldungen werden immer gesendet.',
  kind_job_assigned: 'Mir wird ein Befundungspaket zugewiesen',
  kind_job_returned: 'Ein Paket von mir wird mit Kommentaren zurückgegeben',
  kind_adjudication_assigned: 'Mir werden Abweichungen zur Entscheidung zugewiesen',
  kind_upload_finished: 'Mein Upload ist abgeschlossen',
  kind_ethics_expiring: 'Das Ethikvotum meines Standorts läuft bald ab',
  kind_ethics_ended: 'Das Ethikvotum meines Standorts ist abgelaufen',
  notice_job_assigned: '{site}: neues Befundungspaket in „{task}“ (Bilder: {images})',
  notice_job_returned: '{site}: „{task}“ wurde zurückgegeben (Kommentare: {comments})',
  notice_adjudication_assigned: '{site}: Sie adjudizieren „{task}“',
  notice_upload_finished: '{site}: Upload abgeschlossen, {accepted} angenommen, {duplicates} bereits vorhanden, {rejected} abgelehnt',
  notice_ethics_expiring: '{site}: Das Ethikvotum endet am {until}',
  notice_ethics_ended: '{site}: Das Ethikvotum ist am {until} abgelaufen',
  kind_ops_alert: 'Ein Problem der Plattform wird festgestellt',
  kind_ops_resolved: 'Ein Problem der Plattform ist behoben',
  notice_ops_alert: 'Alarm: {de}',
  notice_ops_resolved: 'Behoben: {de}',
  platformStatus: 'Status',
  services: 'Dienste',
  serviceUp: 'Läuft',
  serviceDown: 'Ausgefallen',
  noGpu: 'Keine GPU',
  queues: 'Warteschlangen',
  queue_uploads: 'Dateien vor der Prüfung',
  queue_exports: 'Exporte wartend oder in Arbeit',
  queue_model_runs: 'Modellläufe wartend oder laufend',
  queue_fingerprints: 'Bilder vor ihrem Duplikat-Fingerabdruck',
  queue_failed_emails: 'Nicht zustellbare E-Mails',
  queue_file_deletions: 'Dateilöschungen, die auf ihren Speicher warten',
  queue_ledger_entries: 'Noch nicht geschriebene Einträge des Wiederherstellungsprotokolls',
  storageUse: 'Speicher',
  storage: 'Speicher',
  sharing: 'Freigaben',
  importsFound: 'Annotationssätze in diesen Dateien: {sets}, davon {pending} warten auf Ihre Entscheidung',
  importNoAuthor: 'Ohne Autor',
  importTo: 'Wessen Sätze sind das',
  importExternal: 'Eine externe Person dieses Namens',
  importNobody: 'Niemandes: weglassen',
  importAssign: 'Zuordnen',
  import_pending: 'offen',
  import_imported: 'übernommen',
  import_conflict: 'die Person hatte schon einen Satz',
  import_invalid: 'Labels passen nicht zum Labelkatalog',
  import_discarded: 'weggelassen',
  import_changed: 'ändern Ihren eigenen Satz',
  proposedChanges: 'Die Dateien ändern Ihre eigenen Annotationen hier:',
  acceptChanges: 'Änderungen übernehmen',
  discardChanges: 'Verwerfen',
  secondFactor: 'Zweiter Faktor',
  secondFactorHint: 'Wen diese Regeln erfassen, richtet bei der nächsten Anmeldung eine Authenticator-App ein, bevor sich die Plattform öffnet.',
  secondFactorFor: 'Verpflichtend für',
  secondFactorAll: 'Alle Personen',
  secondFactorSite: 'Mitglieder von {site}',
  secondFactorRole: 'Alle mit der Rolle {role}',
  secondFactorRequire: 'Verlangen',
  ssoTitle: 'Anmeldung über den Identitätsanbieter des Standorts',
  ssoHint: 'Mitglieder melden sich über den OpenID-Connect-Anbieter des Standorts, etwa Entra ID, mit ihrem Krankenhauskonto an; wer darf, entscheidet der Anbieter. Tragen Sie dort die Redirect-URI unten ein. Bestehende Konten werden nach einer Anmeldung mit dem Plattformpasswort verknüpft.',
  ssoIssuer: 'Anbieter',
  ssoRedirect: 'Redirect-URI',
  ssoName: 'Name auf der Anmeldeseite',
  ssoDiscovery: 'Discovery-Adresse',
  ssoClient: 'Client-ID',
  ssoSecret: 'Client-Secret',
  ssoSecretKept: 'Leer lassen, um das gespeicherte Secret zu behalten.',
  ssoConnect: 'Verbinden',
  ssoDisconnect: 'Trennen',
  supportAccess: 'Supportzugriff',
  supportHint: 'Plattform-Admins sehen keine Bilddaten des Standorts, außer Sie erlauben es hier: nur lesend, höchstens 30 Tage und im Audit-Log vermerkt.',
  grantSupport: 'Zugriff erlauben',
  platformAdmin: 'Plattform-Admin',
  labels: 'Labels',
  labelType: 'Typ',
  type_tag: 'Bild-Tag',
  type_rectangle: 'Rechteck',
  boxRotation: 'Drehung (°)',
  boxRotationHint: 'Im Uhrzeigersinn. Äußeren Griff ziehen oder einen Winkel eingeben.',
  boxRotationInvalid: '0–360° eingeben; alle Ecken müssen im Bild bleiben.',
  type_polygon: 'Polygon',
  type_polyline: 'Linienzug',
  type_points: 'Punkte',
  type_mask: 'Maske',
  labelColor: 'Farbe',
  labelShortcut: 'Taste',
  labelNameHint: 'neuer_labelname',
  addLabel: 'Label hinzufügen',
  attributeValues: 'Werte',
  attributeValuesHint: 'Auswahlwerte, oder Minimum, Maximum, Schritt',
  attributeDefault: 'Vorgabe',
  addAttribute: 'Attribut hinzufügen',
  confirmLabelChange: 'Trotzdem ändern',
  guidelines: 'Annotationsrichtlinien',
  guidelinesVersion: 'Richtlinien, Version {version}',
  guidelinesVersions: 'Versionen',
  guidelinesNone: 'Es gibt noch keine Richtlinien.',
  guidelinesEdit: 'Neue Version schreiben',
  guidelinesWrite: 'Richtlinien schreiben',
  guidelinesText: 'Text',
  guidelinesHint: 'Markdown: # Überschriften, - Listen, **fett**. Jedes Speichern ergibt eine neue Version; Annotationssätze vermerken, unter welcher Version sie entstanden.',
  guidelinesImage: 'Beispielbild hinzufügen',
  guidelinesImageHint: 'PNG, JPEG oder WebP bis 5 MB, ohne identifizierende Daten.',
  guidelinesSave: 'Als neue Version speichern',
  retentionUntil: 'Daten aufbewahren bis',
  retentionExpiring: 'Die Aufbewahrungsfrist endet am {date} (in {days} Tagen): Verlängern Sie sie mit Begründung, lassen Sie das Token des Standorts vernichten oder löschen Sie die Daten des Standorts.',
  retentionExpired: 'Die Aufbewahrungsfrist ist abgelaufen: Verlängern Sie sie mit Begründung, lassen Sie das Token des Standorts vernichten oder löschen Sie die Daten des Standorts. Nichts wird von selbst gelöscht.',
  retentionReason: 'Begründung',
  retentionReasonHint: 'Warum die Daten bis dahin aufbewahrt werden, etwa das Studienprotokoll. Wird im Audit-Log vermerkt.',
  kind_retention_ending: 'Die Aufbewahrungsfrist meines Standorts läuft bald ab',
  notice_retention_ending: '{site}: Die Aufbewahrungsfrist endet am {until}',
  myRoles: 'Meine Rollen',
  allow_site_users: 'Personen des Standorts verwalten',
  allow_site_storage: 'Speicher verwalten',
  allow_site_keys: 'Anonymisierungstoken verwalten',
  allow_site_sharing: 'über Freigaben entscheiden',
  allow_site_audit: 'Audit-Log einsehen',
  allow_site_inference: 'Inferenzschlüssel des Standorts verwalten',
  allow_site_upload: 'Studien des Standorts hochladen',
  allow_site_annotate: 'die Studien öffnen und annotieren, die der Standort und seine Freigaben zeigen, und Befundungspakete bearbeiten',
  allow_site_coordinate: 'Sammlungen, Jobs, eingefrorene Sammlungen, Benchmarks und Lehrfreigaben des Standorts organisieren',
  allow_platform_sites: 'Standorte, Plattformeinstellungen, Modelle und die Statusseite verwalten',
  allow_platform_inference: 'Standorten den Inferenzdienst gewähren und Modellversionen dafür freigeben',
  allow_platform_appoint: 'Lehrende und Katalog-Bearbeitende ernennen',
  allow_catalog: 'den Label-Katalog und die Annotationsrichtlinien pflegen',
  allow_education_teach: 'Kurse anlegen, Lernende einladen, Fallsammlungen anbieten und den Fortschritt der Lernenden verfolgen',
  allow_education_learn: 'die angebotenen Fallsammlungen lesen',
  allow_job_read: 'die Studien des eigenen Auftrags befunden und ihn abschließen',
  allow_job_review: 'abgeschlossene Aufträge prüfen und mit Kommentaren zurückgeben',
  allow_job_adjudicate: 'die zugewiesenen Abweichungen auflösen',
  apiTokens: 'API-Token',
  apiTokensIntro: 'Für Skripte und den Kommandozeilen-Upload. Ein Token handelt in Ihrem Namen, nie mit mehr Rechten, und bis zu seinem letzten Tag. Die API ist unter /api/docs dokumentiert.',
  apiTokenShownOnce: 'Kopieren Sie das Token jetzt: Es wird nur dieses eine Mal angezeigt.',
  tokenScope: 'Rechte',
  tokenRead: 'Nur lesen',
  tokenWrite: 'Alles, was ich darf',
  tokenNameHint: 'Zum Beispiel: Kommandozeilen-Upload auf meinem Laptop',
  tokenName: 'Name des neuen Tokens',
  tokenLastDay: 'Letzter Tag',
  tokenUntil: 'bis {date}',
  tokenLastUsed: 'zuletzt benutzt {date}',
  createApiToken: 'Token erstellen',
  deviceTitle: 'Kommandozeilen-Upload verbinden',
  deviceCode: 'Code aus pedfx-upload',
  deviceContinue: 'Weiter',
  deviceAsks: '{client} möchte in Ihrem Namen hochladen und arbeiten.',
  deviceWarning: 'Bestätigen Sie nur, wenn Sie dies selbst auf Ihrem Rechner begonnen haben und pedfx-upload diesen Code anzeigt. Niemand von der Plattform wird Sie je danach fragen.',
  deviceApprove: 'Verbinden',
  deviceDeny: 'Ablehnen',
  deviceApproved: 'Verbunden. pedfx-upload fährt selbst fort; Sie können diese Seite schließen. Unter „API-Token“ in Ihrem Profil können Sie die Verbindung trennen.',
  deviceDenied: 'Abgelehnt. pedfx-upload ist nicht verbunden.',
  privacyNotice: 'Datenschutzhinweis',
  imprint: 'Impressum',
  termsOfUse: 'Nutzungsbedingungen',
  legalTexts: 'Rechtliches',
  userManual: 'Benutzerhandbuch',
  acceptTermsOfUse: 'Ich akzeptiere die Nutzungsbedingungen',
  outsideAccess: 'Von außerhalb des Standorts geöffnet',
  opened_task: 'hat den Auftrag geöffnet',
  opened_dataset: 'hat die eingefrorene Sammlung geöffnet',
  opened_benchmark: 'hat Benchmark-Fälle heruntergeladen',
  opened_collection: 'hat die Sammlung geöffnet',
  split: 'Aufteilung',
  withExport: 'mit Ihnen geteilt, mit Export',
  viewOnly: 'mit Ihnen geteilt, nur ansehen',
  annotationSet: 'Annotationssatz',
  shareToUser: 'Eine Person',
  shareToSite: 'Einen ganzen Standort',
  openDataset: 'Öffnen',
  sharingPolicy: 'Daten des Standorts verlassen ihn',
  policy_forbidden: 'Nicht erlaubt',
  policy_approval: 'Nur mit Zustimmung eines Standort-Admins',
  policy_allowed: 'Erlaubt',
  approve: 'Zustimmen',
  refuse: 'Ablehnen',
  sharedWithMe: 'Mit mir geteilt',
  share: 'Teilen',
  shareValidUntil: 'Letzter Tag des Zugriffs (optional)',
  revoke: 'Widerrufen',
  recipient: 'Empfänger',
  shareState_pending_approval: 'Wartet auf Zustimmung',
  shareState_refused: 'Abgelehnt',
  shareState_awaiting_acceptance: 'Wartet auf Annahme',
  shareState_active: 'Aktiv',
  shareState_expired: 'Abgelaufen',
  shareState_revoked: 'Widerrufen',
  dataUseTerms: 'Nutzungsbedingungen',
  acceptTerms: 'Nutzungsbedingungen akzeptieren',
  sharedUntil: 'geteilt bis {date}',
  kind_grant_received: 'Mir werden Studien freigegeben',
  siteData: 'Die Daten des Standorts',
  siteLeaving: 'Der Standort verlässt die Plattform. Ab dem {date} löscht die Plattform seine Bilder und alles daraus Abgeleitete.',
  siteDataDeleting: 'Die Daten des Standorts werden seit dem {date} gelöscht.',
  siteDataDeleted: 'Die Daten des Standorts wurden am {date} gelöscht.',
  siteAnnotationsHint: 'Jeder Annotationssatz, jede Erstbefundung und jede Auflösung auf den Studien des Standorts, Bild für Bild, als JSON.',
  siteAnnotationsDownload: 'Annotationen des Standorts herunterladen',
  siteDataDelete: 'Alle Daten des Standorts löschen',
  siteDataDeleteHint: 'Löscht die Bilder des Standorts mit jeder Annotation, Erstbefundung, Auflösung und Vorhersage darauf; sie werden aus den eingefrorenen Sammlungen entfernt, die sie enthalten. Laden Sie zuerst die Annotationen herunter. Das lässt sich nicht rückgängig machen.',
  typeNameToConfirm: 'Geben Sie „{name}“ zur Bestätigung ein',
  siteTokenDestroy: 'Vernichtung des Tokens erfassen',
  tokenDestroyed: 'Token vernichtet',
  siteTokenDestroyHint: 'Erfassen Sie sie, sobald die Schlüsselverwaltung das Token und sein Wiederherstellungsblatt vernichtet hat und die eingeschriebenen Personen ihre Kopien gelöscht haben. Seine Schlüssel-ID wird stillgelegt: Mit dem Token wird kein Upload mehr angenommen, und Browser vergessen es, wenn sie die Plattform das nächste Mal öffnen. Die Bilder bleiben nutzbar, und ihre Pseudonyme lassen sich nicht mehr aus Patienten-IDs ableiten. Das beantwortet die Erinnerungen an die Aufbewahrungsfrist.',
  siteTokenDestroyed: 'Das Token des Standorts wurde vernichtet, erfasst am {date}. Die Bilder bleiben nutzbar; ihre Pseudonyme lassen sich nicht mehr aus Patienten-IDs ableiten.',
  siteNoticeUntil: 'Kündigungsfrist bis',
  siteRecordDeparture: 'Austritt des Standorts erfassen',
  siteCancelDeparture: 'Der Standort bleibt',
  kind_site_departure: 'Mein Standort verlässt die Plattform',
  notice_site_departure: '{site} verlässt die Plattform: Laden Sie die Annotationen vor dem {until} herunter',
  kind_teaching_approval: 'Eine Lehrfreigabe wartet auf die Zustimmung meines Standorts',
  notice_teaching_approval: '{dataset} ist für die Lehre freigegeben und enthält Bilder von {site}: Bitte zustimmen oder ablehnen',
  kind_model_released: 'Eine Modellversion wird für den Inferenzdienst freigegeben',
  notice_model_released: '{model} {version} ist für den Inferenzdienst freigegeben',
  kind_model_retired: 'Eine Modellversion verlässt den Inferenzdienst',
  notice_model_retired: '{model} {version} ist im Inferenzdienst nicht mehr verfügbar',
  notice_grant_received: '{site} hat Ihnen seine Studien freigegeben',
  grants: 'Freigaben',
  grantsHint: 'Teilen Sie die Studien Ihres Standorts mit einem anderen Standort oder benannten Personen dort, zum Ansehen oder Annotieren. Empfangende akzeptieren die Bedingungen und sehen nur gerenderte Ansichten; ein Widerruf beendet den Zugriff sofort.',
  grantsReceived: 'Erhaltene Freigaben',
  grantEveryone: 'alle Mitglieder',
  grantNoEnd: 'ohne Enddatum',
  grantAccess: 'Zugriff',
  grantView: 'Ansehen',
  grantAnnotate: 'Ansehen und annotieren',
  grantExport: 'Ansehen und exportieren',
  grantWithdrawn: 'Widerrufen',
  grantExpired: 'Abgelaufen',
  grantWithdraw: 'Widerrufen',
  grantWithdrawWarning: 'Der Zugriff endet sofort.',
  grantUsers: 'Benannte Personen (optional)',
  grantUsersHint: 'E-Mail-Adressen von Mitgliedern dieses Standorts; ohne Angabe gilt die Freigabe für alle Mitglieder.',
  grantAgreement: 'Datenweitergabevereinbarung',
  grantAgreementHint: 'Die Vereinbarung, auf der die Freigabe beruht, etwa mit Aktenzeichen und Datum.',
  grantTransferBasis: 'Rechtsgrundlage der Übermittlung',
  grantTransferBasisHint: 'Der Standort liegt außerhalb des EWR oder hat kein erfasstes Land: Nennen Sie die Grundlage nach DSGVO Art. 44 bis 49, etwa Standardvertragsklauseln.',
  grantCreate: 'Freigeben',
  grantForbidden: 'Die Freigaberichtlinie hält die Daten am Standort, daher gibt er nichts frei.',
  grantFrom: 'Studien von {site}',
  grantOpen: 'Auf der Bilderseite öffnen',
  grantRenderedNotice: 'Sie sehen diese Studien als auf dem Server gerenderte, für Sie markierte Ansichten.',
  siteCountry: 'Land',
  siteCountryHint: 'Zweibuchstabiger Code, etwa AT. Freigaben an Standorte außerhalb des EWR brauchen eine Übermittlungsgrundlage.',
  notice_account_locked: 'Ihr Konto wurde nach wiederholten fehlgeschlagenen Anmeldungen vorübergehend gesperrt',
  storageHosted: 'Gehosteter Speicher der Plattform',
  storageUploadsGo: 'Neue Uploads gehen in',
  storageEndpoint: 'S3-Endpunkt',
  storageRegion: 'Region',
  storageBucket: 'Bucket',
  storagePrefix: 'Ordner (optional, endet mit /)',
  storageAccessKey: 'Zugriffsschlüssel-ID',
  storageSecret: 'Geheimer Zugriffsschlüssel',
  storageCredentialsHint: 'Wird verschlüsselt gespeichert und nie wieder angezeigt.',
  storageTestAndAdd: 'Testen und hinzufügen',
  storageTestAgain: 'Im Browser erneut testen',
  storageReplace: 'Zugangsdaten ersetzen',
  connectorHint: 'Ein Standort-Connector läuft im Netz Ihres Standorts neben dem Speicher (ein Ordner, etwa ein NAS, oder ein S3-kompatibler Bucket) und hält eine ausgehende Verbindung zur Plattform. Die Zugangsdaten des Speichers bleiben beim Connector; Bilder werden durch die Plattform nur durchgereicht. Erzeugen Sie einen Einmalcode, führen Sie den Befehl auf einem Rechner mit Docker aus und testen und aktivieren Sie den neuen Speicherort dann hier.',
  connectorCode: 'Registrierungscode erzeugen',
  connectorRun: 'Innerhalb eines Tages auf dem Rechner des Connectors ausführen; der Code gilt einmal:',
  connectorEnvironment: 'mit einer Datei connector.env daneben, die den Speicher nennt:',
  connectorLocation: 'Standort-Connector',
  connectorConnected: 'verbunden',
  connectorOffline: 'nicht verbunden',
  connectorNone: 'kein Connector',
  connectorReplace: 'Connector ersetzen',
  connectorReplaceCode: 'Code für den neuen Connector erzeugen',
  connectorRevoke: 'Connector widerrufen',
  storageActive: 'Aktiv seit {date}',
  storageInactive: 'Nicht aktiv: Der Browser-Test fehlt',
  storageLost: 'Am {date} als verloren erklärt',
  storageCopy: 'Auf eine Kopie umstellen',
  storageCopyHint: 'Für eine Kopie dieses Buckets, die der Standort angelegt hat, auch eines verlorenen. Die Plattform prüft die Kopie wie einen neuen Bucket und stellt erst um, wenn sie die Datei jedes Bildes von hier enthält; der Browser-Test folgt. Wählen Sie zuerst einen anderen Speicherort für Uploads.',
  storageCopySwitch: 'Kopie prüfen und umstellen',
  storageCopyMissing: 'Der Kopie fehlen {count} Bilddateien. Kopieren Sie auch diese und versuchen Sie es erneut.',
  storageDeclareLost: 'Als verloren erklären',
  storageFound: 'Wiedergefunden',
  storageLostWarning: 'Seine Bilder bleiben mit ihren Annotationen gelistet, öffnen sich aber als verloren, bis sie erneut hochgeladen werden. Uploads gehen nicht mehr dorthin.',
  storageLostConfirm: 'Geben Sie zur Bestätigung den Namen des Speicherorts ein',
  storageStep_write: 'schreiben',
  storageStep_read: 'lesen',
  storageStep_list: 'auflisten',
  storageStep_private: 'nicht öffentlich',
  storageStep_delete: 'löschen',
  storageBrowserFailed: 'Ihr Browser konnte nicht in den Bucket schreiben und daraus lesen. Tragen Sie die CORS-Regel unten im Bucket ein und testen Sie erneut.',
  storageCors: 'Browser laden Bilder direkt aus dem Bucket, daher braucht er diese CORS-Regel:',
  storageNoKey: 'Für eigene Buckets der Standorte braucht der Server einen Speicherschlüssel (PEDFX_STORAGE_KEY).',
  storageSize: 'Größe',
  storageSummary: '{images} Bilder; Uploads {uploads}, Exporte {exports}.',
  diskFree: '{free} frei von {total}',
  certificateUntil: 'Zertifikat gültig bis {until}',
  lastSeen: 'zuletzt gemeldet {when}',
  backups: 'Sicherungen',
  backupOk: 'Letzte Sicherung erfolgreich',
  backupFailed: 'Letzte Sicherung fehlgeschlagen',
  backupNone: 'Noch keine Sicherung',
  lastBackup: 'Letzte erfolgreiche Sicherung',
  lastRestoreCheck: 'Letzte Wiederherstellungsprobe',
  lastLedgerCopy: 'Wiederherstellungsprotokoll zuletzt extern kopiert',
  patientLookup: 'Patient finden oder löschen',
  patientLookupIntro: 'Ursprüngliche Patienten-ID genau wie in den DICOM-Dateien eingeben, dazu das Standort-Token. Das Pseudonym wird in diesem Browser berechnet; nur es wird an die Plattform gesendet.',
  siteToken: 'Standort-Token',
  patientWarnings: 'Zu prüfende Patientinnen und Patienten',
  patientAssociations: 'Patienten-IDs einer Person',
  patientAssociationsIntro: 'Ordnen Sie zwei Patienten-IDs derselben Person zu, etwa nachdem das Krankenhaus sie zusammengeführt hat. Beide Pseudonyme und der Unterschied ihrer Datumsverschiebungen werden in diesem Browser berechnet; nur sie werden gesendet. Gespeicherte Dateien bleiben unverändert; Aufteilungen, die weiteren Studien, Zählungen und Suchen folgen der Zuordnung.',
  associationFirst: 'Patienten-ID',
  associationSecond: 'Patienten-ID derselben Person',
  associate: 'Zuordnen',
  dissociate: 'Aufheben',
  patientWarningsIntro: 'Die Bilder dieser Patientinnen und Patienten widersprechen sich. Vergleichen Sie sie mit dem Archiv und korrigieren Sie falsche Metadaten; die Plattform ändert nichts von sich aus.',
  patientWarningSex: 'Geschlechter {sexes}',
  patientWarningBirthDate: 'verschiedene Geburtsdaten',
  patientId: 'Patienten-ID',
  findPatient: 'Bilder finden',
  patientWithToken: 'Mit dem Token',
  patientWithoutToken: 'Ohne Token',
  patientWithoutTokenIntro: 'Wählen Sie die Bilder des Patienten aus Ihrem Archiv, etwa seine ursprünglichen DICOM-Dateien. Dieser Browser berechnet einen Fingerabdruck der Pixel jedes Bildes, die die Plattform nie verändert; nur die Fingerabdrücke werden gesendet, und die Bilder des Standorts mit denselben Pixeln führen zum Patienten.',
  patientArchiveImages: 'Archivbilder auswählen',
  patientImagesUnread: 'Keine dieser Dateien ließ sich als DICOM-Bild lesen.',
  patientImagesSkipped: 'Dateien, die sich nicht als DICOM-Bild lesen ließen: {count}',
  tokenNotOfSite: 'Dieses Token gehört nicht zu diesem Standort.',
  patientFound: '{count} Bilder dieses Patienten an diesem Standort. Pseudonym',
  patientFound_one: '1 Bild dieses Patienten an diesem Standort. Pseudonym',
  deletePatient: 'Alle {count} Bilder und ihre Annotationen löschen',
  deletePatient_one: 'Das Bild und seine Annotationen löschen',
  deletePatientWarning: 'Löscht die Dateien, Annotationen und Vorhersagen dieser Bilder, die aus den eingefrorenen Sammlungen entfernt werden, die sie enthalten, und die Exporte, die sie enthielten. Das lässt sich nicht rückgängig machen.',
  patientDeleted: '{images} Bilder gelöscht; sie wurden aus {datasets} eingefrorenen Sammlungen entfernt.',
  patientFilesWaiting: 'Die Dateien von {count} Bildern liegen an einem Speicherort, der nicht antwortet. Sie werden gelöscht, sobald er wieder antwortet.',
  dashboard: 'Dashboard',
  studiesPerSite: 'Studien je Standort',
  assignmentsPerJob: 'Pakete je Auftrag',
  agreementPerJob: 'Übereinstimmung der Befundenden: einen Auftrag öffnen.',
  readerProgress: 'Befundende',
  readingTime: 'Mittlere Befundungszeit je abgeschlossenem Bild',
  perImage: 'Bild',
  annotationsPerLabel: 'Annotationen je Klasse',
  dpa: 'Auftragsverarbeitungsvertrag',
  dpaValid: 'Gültig: Uploads erlaubt',
  dpaMissing: 'Fehlt oder abgelaufen: Uploads gesperrt',
  dpaVersion: 'Version',
  dpaSignedOn: 'Unterzeichnet am',
  dpaValidUntil: 'Gültig bis (optional)',
  ethicsExpired: 'Das Ethikvotum ist abgelaufen. Nichts wird gesperrt: Erfassen Sie hier die Verlängerung.',
  ethicsExpiring: 'Das Ethikvotum endet am {date}, in {days} Tagen.',
  auditLog: 'Audit-Protokoll',
  auditSite: 'Einträge dieses Standorts.',
  auditAll: 'Alle Einträge der Plattform.',
  auditActionFilter: 'Aktion, z. B. upload. oder collection.open',
  filter: 'Filtern',
  exportCsv: 'CSV exportieren',
  verifyChain: 'Hash-Kette prüfen',
  chainIntact: 'Die Hash-Kette ist intakt: kein Eintrag wurde geändert oder entfernt.',
  chainBroken: 'Die Hash-Kette bricht bei Eintrag {id}.',
  when: 'Wann',
  who: 'Wer',
  auditAction: 'Aktion',
  auditTarget: 'Ziel',
  loadOlder: 'Ältere Einträge laden',
  showMore: '{count} weitere zeigen',
  andMore: '+{count} weitere',
  onlyDisagreeing: 'Nur Bilder, bei denen die Lesenden abweichen',
  correctMetadata: 'Metadaten korrigieren',
  applyToStudy: 'Auf alle {count} Ansichten der Studie anwenden',
  history: 'Änderungsverlauf',
  change_added: '{labels} hinzugefügt',
  change_changed: '{labels} geändert',
  change_removed: '{labels} entfernt',
  change_tagged: '{labels} markiert',
  change_untagged: '{labels} aufgehoben',
  patientStudies: 'Studien dieses Patienten',
  studyNumber: 'Studie {number}',
  backToStudy: 'Zurück zu Studie {number}',
  priorStudiesSetting: 'Befundende dürfen frühere und spätere Untersuchungen ihrer Patienten öffnen',
  freeTextSetting: 'Befundende dürfen Freitext-Klassen für Befunde benennen, die kein Label benennt',
  blindedSetting: 'Verblindete Befundung: Bis sie ihr Paket abschließen, sehen Befundende auf seinen Bildern keine Annotationen anderer. Ausgeschaltet sehen sie eine Heatmap der Rechtecke aller, die ein Bild abgeschlossen haben',
  aiSuggestionsSetting: 'KI-Vorschläge für Befundende',
  aiOff: 'Aus',
  aiOnRequest: 'Auf Anforderung',
  aiAtOnce: 'Sofort gezeigt',
  aiAtOnceBadge: 'KI-Vorschläge sofort gezeigt',
  jobReading: 'Befundung',
  jobLabels: 'Angebotene Labels ({count})',
  jobLabelsHint: 'Im Auftrag fügen Befundende nur diese Labels hinzu; ihre bisherigen Annotationen bleiben. Ohne Auswahl bietet der Auftrag alle Labels an.',
  jobInstructions: 'Anweisungen für die Befundenden',
  freshReaders: 'Studien an Befundende geben, die sie noch nicht annotiert haben, solange genug übrig sind',
  annotatedBefore: '{count} Studien bereits annotiert',
  cannotOpen: 'kann {count} Studien nicht öffnen',
  cannotOpen_one: 'kann 1 Studie nicht öffnen',
  whichStudies: 'Welche Studien',
  earlierWork: 'Vor dem Auftrag annotiert',
  coReader: 'Mitlesende Person {n}',
  deleteJob: 'Auftrag löschen',
  endJob: 'Auftrag schließen oder löschen',
  closeJob: 'Auftrag schließen',
  reopenJob: 'Auftrag wieder öffnen',
  closeJobHint: 'Ein geschlossener Auftrag ist schreibgeschützt: Befundende ändern oder beenden ihre Pakete nicht mehr, Reviews und Auflösungen bleiben, wie sie sind. Verblindung und andere Leseregeln gelten nicht mehr.',
  jobClosed: 'Geschlossen',
  deleteJobHint: 'Entfernt die Pakete, Erstbefundungen und Auflösungen dieses Auftrags. Die Studien, die Annotationen aller Befundenden und die daraus eingefrorenen Sammlungen bleiben.',
  deleteJobConfirm: '„{name}“ endgültig löschen',
  labelCatalog: 'Label-Katalog',
  labelCatalogIntro: 'Ein Katalog von Labels für alle Standorte, Aufträge, Fallsammlungen und Labeling-Profile, gepflegt von der Katalog-Redaktion. Aufträge und Fallsammlungen bieten Teile davon an. Ein verwendetes Label behält Namen und Typ für immer und wird stillgelegt statt entfernt; ein stillgelegtes Label, das nichts verwendet oder nennt, lässt sich löschen. Fehlt ein Label? Schlagen Sie es hier vor und benennen Sie bis dahin im Werkzeug eine Freitext-Klasse.',
  catalogEditor: 'Katalog-Bearbeitung',
  catalogEditors: 'Katalog-Redaktion',
  catalogEditorsHint: 'Die Katalog-Redaktion fügt Labels hinzu, ändert sie und schreibt die Annotationsrichtlinien. Fügen Sie Personen über die E-Mail-Adresse ihres Kontos hinzu.',
  addCatalogEditor: 'Zur Katalog-Redaktion hinzufügen',
  jobLabelsOffered: 'Labels: {labels}',
  allLabels: 'alle',
  blindedBadge: 'Verblindet bis zum Abschluss',
  noAiBadge: 'Ohne KI-Vorschläge',
  noPriorsBadge: 'Ohne Voruntersuchungen',
  noFreeTextBadge: 'Ohne Freitext-Klassen',
  offsetSameDay: 'Selber Tag',
  offsetUnknown: 'Zeitpunkt unbekannt',
  offsetEarlier: '{amount} früher',
  offsetLater: '{amount} später',
  offsetDays: '{count} T.',
  offsetWeeks: '{count} Wo.',
  offsetMonths: '{count} Mon.',
  offsetYears: '{count} J.',
  review: 'Überprüfen',
  reader: 'Befundende Person',
  noFindings: 'keine Befunde',
  importedSet: 'importiert',
  importedFrom: 'importiert von {author}',
  reviewComments: 'Kommentare der Überprüfung',
  clickToComment: 'Auf eine Stelle im Bild klicken, um dort einen Kommentar anzuheften.',
  comment: 'Kommentar',
  addComment: 'Kommentar hinzufügen',
  returnToReader: 'An die befundende Person zurückgeben ({count} Kommentare)',
  returnToReader_one: 'An die befundende Person zurückgeben (1 Kommentar)',
  returnHelp: 'Die befundende Person sieht die Kommentare; die kommentierten Bilder öffnen sich für Änderungen.',
  assignmentReturnedNotice: 'Eine Überprüfung hat dieses Paket mit Kommentaren zurückgegeben. Nur die kommentierten Bilder sind wieder offen.',
  focusHelp: 'Zeigt nur die Bilder und die Zeichenwerkzeuge; dieselbe Taste schaltet zurück.',
  draft_discarded: 'Ungespeicherte Änderungen einer früheren Sitzung beruhten auf einer älteren Fassung und wurden verworfen.',
  modelRuns: 'Modellläufe',
  modelRunsIntro: 'Wendet ein Modell im Hintergrund auf alle Bilder des Auftrags an. Befundende sehen die Vorschläge weiterhin nur auf Anfrage, dann ohne Wartezeit.',
  siteRunsIntro: 'Wendet ein Modell im Hintergrund auf alle Studien an, die Sie öffnen dürfen, so wie sie beim Start sind; die Seite einer Sammlung startet einen Lauf über deren Studien. Befundende sehen die Vorschläge weiterhin nur auf Anfrage, dann ohne Wartezeit.',
  collectionRunsIntro: 'Wendet ein Modell im Hintergrund auf die Bilder der gespeicherten Sammlung am gewählten Standort an, so wie sie beim Start sind. Befundende sehen die Vorschläge weiterhin nur auf Anfrage.',
  noBatchModels: 'Kein Modell für Läufe im Hintergrund ist aktiviert.',
  runAllImages: 'alle Bilder',
  runSelectedImages: 'ausgewählte Bilder',
  startCollectionRun: 'Auf die Bilder der Sammlung anwenden',
  noThumbnail: 'Keine Vorschau',
  imageUnavailable: 'Nicht verfügbar: Der Speicher seines Standorts antwortet nicht',
  retryThumbnails: 'Vorschaubilder erneut laden',
  imageAlt: 'Bild',
  ageShort: '{age} J',
  saveAsCollection: 'Als Sammlung speichern',
  saveAsCollectionHint: 'Diese Filter in eine neue Sammlung übernehmen',
  clearFilters: 'Filter zurücksetzen',
  noImagesMatch: 'Keine Bilder passen zu diesen Filtern.',
  overlays: 'Angaben auf den Bildern',
  groupBy: 'Gruppieren nach',
  groupPatient: 'Patient',
  groupPatientHint: 'Die Bilder jedes Patienten in einem hellen Rahmen',
  groupStudy: 'Studie',
  groupStudyHint: 'Die Bilder jeder Studie in einem blauen Rahmen',
  boxHeatmap: 'Box-Heatmap',
  boxHeatmapHint: 'Die Rechtecke der Annotationssätze, die Sie sehen dürfen, in den Farben ihrer Labels: kräftiger, wo sie sich überlagern.',
  overlayOff: 'Ohne Angaben',
  overlayOffHint: 'Nur die Bilder',
  overlayCorners: 'Ecken',
  overlayCornersHint: 'Alter und Projektion in den unteren Ecken',
  overlayCaptions: 'Bildzeilen',
  overlayCaptionsHint: 'Eine Zeile am unteren Rand: Körperregion, Alter, Geschlecht und Standort',
  galleryView: 'Ansicht',
  galleryViewHint: 'Gruppierung, Box-Heatmap, Angaben auf den Bildern und ihre Größe',
  thumbnailSize: 'Vorschaugröße',
  thumbnailSizeHint: 'Vorschaugröße. Strg + Mausrad über den Bildern ändert sie ebenfalls; ein Doppelklick stellt die Standardgröße von {size} px wieder her.',
  standardSize: 'Standardgröße',
  keyCtrl: 'Strg',
  mouseWheel: 'Mausrad',
  keysMove: 'wechseln',
  keysOpen: 'öffnen',
  keySpace: 'Leertaste',
  keysChoose: 'auswählen',
  keysClear: 'Auswahl aufheben',
  mouseClick: 'Klick',
  mouseDoubleClick: 'Doppelklick',
  mouseDrag: 'Ziehen',
  galleryRange: '{from}–{to} von {total}',
  galleryOnScreen: '{count} Reihen, etwa {perRow} pro Reihe: {shown} sichtbar',
  galleryOnScreen_one: '1 Reihe: {shown} sichtbar',
  searchCollections: 'Nach Name oder Filter suchen',
  anyBodyPart: 'Jede Körperregion',
  sortBy: 'Reihenfolge',
  sortChanged: 'Zuletzt geändert',
  sortName: 'Name',
  sortCreated: 'Neueste',
  collectionCount: '{count} Sammlungen',
  collectionCount_one: '1 Sammlung',
  collectionsShown: '{shown} von {total} Sammlungen',
  noCollectionsYet: 'Noch keine Sammlungen',
  noCollectionsMatch: 'Keine Sammlung passt.',
  browseImages: 'Bilder durchsehen',
  myCollection: 'Meine',
  noFilters: 'Ohne Filter',
  changedOn: 'Geändert am {date}',
  collectionName: 'Name der Sammlung',
  revertChanges: 'Änderungen verwerfen',
  saveAsNew: 'Als neue speichern',
  collectionUses: 'Modellläufe und Lehre',
  collectionTeaching: 'Lehre',
  usesSavedFilters: 'Läufe und das Lehrangebot verwenden die gespeicherten Filter, nicht die ungespeicherten Änderungen.',
  startRun: 'Auf alle Bilder anwenden',
  run_pending: 'In der Warteschlange',
  run_running: 'Läuft',
  run_done: 'Fertig',
  run_failed: 'Fehlgeschlagen',
  run_cancelled: 'Abgebrochen',
  interactiveModelHint: 'Befundende zeigen mit Klicks oder einem Rahmen auf ein Objekt und wählen die Polygon- oder Maskenklasse, die es wird.',
  smartTool: 'Smart',
  smartHelp: 'Einen Rahmen um den fraglichen Bereich ziehen; dann zum Verfeinern in das Objekt klicken oder mit Umschalt+Klick außerhalb. Enter übernimmt die Kontur, Esc beginnt neu.',
  smartNeedsLabel: 'Für das Smart-Werkzeug eine Polygon- oder Maskenklasse wählen.',
  segmenting: 'Segmentiere …',
  modelAnnotation: 'Annotation',
  modelAutomatic: 'Jedes Bild automatisch verarbeiten, sobald es hochgeladen ist, im Hintergrund',
  modelShownAtOnce: 'Vorschläge sofort beim Öffnen eines Bildes zeigen, wo kein Auftrag sie auf Anforderung hält',
  modelAnnotationIntro: 'Wer eine Studie annotieren darf, kann ein aktiviertes Modell um Vorschläge dafür bitten. Ordnen Sie jede Modellklasse einem Katalog-Label zu; nicht zugeordnete Klassen werden ignoriert.',
  enabled: 'Aktiv',
  disabled: 'Aus',
  mapClass: 'Label für',
  notUsed: 'Nicht verwendet',
  enableModel: 'Aktivieren',
  disableModel: 'Deaktivieren',
  suggestWith: 'Vorschläge von {model}',
  heatmapWith: 'Heatmap: {model}',
  heatmapShown: 'Heatmap für {finding} ({confidence}): rote Regionen zählten am meisten.',
  hideHeatmap: 'Ausblenden',
  raterHeatmap: 'Heatmap der Befundenden',
  raterHeatmapOff: 'Aus',
  raterHeatmapHelp: 'Anteil der {count} Befundenden, deren Rechtecke eine Region abdecken: gelb wenige, rot alle.',
  raterHeatmapFailed: 'Die Heatmap der Befundenden ließ sich nicht anzeigen.',
  suggestionsHint: 'Vorschläge erscheinen nur auf Anforderung. Prüfen Sie jeden; Sie bleiben für Übernommenes verantwortlich.',
  reject: 'Verwerfen',
  acceptAll: 'Alle übernehmen',
  minConfidence: 'Mindestkonfidenz',
  rejectAll: 'Alle verwerfen',
  brushHelp: 'Ziehen zum Malen oder Radieren; jeder Strich wird gespeichert. [ und ] ändern die Größe. Eine Maske auswählen, um sie zu ergänzen.',
  outlineHelp: 'Um den Bereich klicken; Doppelklick oder Enter füllt ihn.',
  finishAssignment: 'Paket abschließen',
  assignmentUnavailable: '{count} Bilder dieses Pakets stehen Ihnen nicht mehr zur Verfügung, daher kann es noch nicht abgeschlossen werden. Bitte informieren Sie die Koordination des Auftrags.',
  assignmentUnavailable_one: '1 Bild dieses Pakets steht Ihnen nicht mehr zur Verfügung, daher kann es noch nicht abgeschlossen werden. Bitte informieren Sie die Koordination des Auftrags.',
  finishedReadOnly: 'Dieses Bild ist abgeschlossen. Öffnen Sie es wieder, um Ihre Annotationen zu ändern.',
  selectTool: 'Auswählen',
  undo: 'Rückgängig',
  redo: 'Wiederholen',
  sidePanel: 'Seitenleiste',
  findClass: 'Klasse suchen',
  imageTags: 'Bild-Tags',
  objects: 'Objekte',
  noIdentifyingData: 'Keine identifizierenden Patientendaten eingeben.',
  save_loading: 'Wird geladen …',
  save_saved: 'Gespeichert',
  save_unsaved: 'Ungespeicherte Änderungen',
  save_saving: 'Wird gespeichert …',
  save_retrying: 'Offline, neuer Versuch läuft',
  save_conflict: 'In einem anderen Fenster geändert',
  save_failed: 'Nicht gespeichert',
  autosaveIdle: 'Speichert automatisch',
  autosaveEditing: 'Speichert beim Verlassen des Felds',
  loadStored: 'Gespeicherte Version laden',

  agreement: 'Übereinstimmung',
  agreementIntro:
    'Über die Erstbefundungen beider Befundenden, festgehalten, als sie ihr Paket abgeschlossen haben, sodass spätere Änderungen nichts verändern: Kappa für Bild-Tags; F1 für Befunde, dazu IoU und Dice der markierten Flächen für Rechtecke und Polygone.',
  agreementNeedsPairs: 'Die Übereinstimmung wird berechnet, sobald zwei Lesende Pakete mit denselben Bildern abgeschlossen haben.',
  label: 'Label',
  marked: 'Markiert',
  bothReaders: 'Beide',
  resolution: 'Konsens',
  majorityIntro:
    'Behält Befunde, die mindestens so viele Befundende mit demselben Label markiert haben, verwirft Befunde, die mindestens so viele nicht markiert haben, und lässt den Rest strittig. Von einer Adjudikation entschiedene Bilder bleiben unverändert.',
  minAgreeing: 'Mindestzahl übereinstimmender Befundender',
  maskMethod: 'Masken',
  maskMajority: 'Pixel, die mindestens die Hälfte markiert hat',
  maskStaple: 'STAPLE: Befundende nach geschätzter Zuverlässigkeit gewichtet',
  resolveMajority: 'Per Mehrheit entscheiden',
  majorityDone: '{images} Bilder entschieden, {disputed} Befunde strittig, {skipped} Bilder übersprungen.',
  comparisonSettings: 'Vergleichseinstellungen',
  matchIou: 'Gleicher Befund ab Überlappung (IoU)',
  agreeIou: 'Gleiche Lage ab Überlappung (IoU)',
  matchDistance: 'Linien und Punkte: gleicher Befund innerhalb (Anteil der Bilddiagonale)',
  agreeDistance: 'Linien und Punkte: gleiche Lage innerhalb (Anteil der Bilddiagonale)',
  numericTolerance: 'Toleranz für numerische Attribute',
  readCount: 'Befundet',
  agreed: 'Übereinstimmend',
  discrepant: 'Diskrepant',
  compare: 'Vergleichen',
  notCompared: 'Verglichen wird, sobald zwei Befundende das Bild abgeschlossen haben.',
  findings: 'Befunde',
  showResolution: 'Konsens anzeigen',
  missingBy: 'Nicht markiert von {names}',
  experienceYears: '{years} Jahre',
  resolution_majority: 'Mehrheit',
  resolution_adjudication: 'Adjudiziert',
  resolution_outdated: 'Veraltet',
  reason_missed: 'übersehen',
  reason_label: 'Label',
  reason_location: 'Lage',
  reason_attribute: 'Attribute',
  finding_agreed: 'Übereinstimmend',
  finding_resolved: 'Entschieden',
  finding_disputed: 'Strittig',
  present: 'vorhanden',
  absent: 'nicht vorhanden',
  adjudication: 'Adjudikation',
  adjudicator: 'Adjudikator:in',
  jobRoles: 'Prüfung und Adjudikation',
  jobRolesHint: 'Prüfende sehen abgeschlossene Pakete durch und geben sie zurück; die Adjudikation entscheidet die Diskrepanzen. Beide befunden den Auftrag nicht und müssen jede seiner Studien öffnen dürfen.',
  reviewers: 'Prüfende',
  nobody: 'Niemand',
  adjudicate: 'Entscheiden',
  adjudicatedImages: '{count} Bilder adjudiziert',
  adjudicatedImages_one: '1 Bild adjudiziert',
  nothingToAdjudicate: 'Es gibt noch keine Diskrepanzen zu entscheiden.',
  accept: 'Übernehmen',
  notPresent: 'Nicht vorhanden',
  merge: 'Zusammenführen',
  markDisputed: 'Strittig',
  saveDecisions: 'Entscheidungen speichern',
  evidence: 'Beleg',
  noEvidence: 'Keine spätere Untersuchung zitiert',
  laterStudy: 'Untersuchung {days} Tage später',
  openStudy: 'Untersuchung öffnen',
  decidedOf: '{done} von {total} entschieden',
  datasets: 'Eingefrorene Sammlungen',
  newDataset: 'Studien einfrieren',
  datasetIntro: 'Eine eingefrorene Sammlung hält Studien, die aus einem Auftrag gewählten Annotationen und eine Aufteilung nach Patient:innen fest. Sie ändert sich nie, und jeder Export trägt ihre Prüfsumme.',
  sourceJob: 'Auftrag',
  annotationChoice: 'Annotationen',
  choice_resolution: 'Konsens',
  choice_none: 'Keine, nur die Studien',
  choice_checked: 'Sets derer, die jede Studie geprüft haben (ohne Auftrag)',
  frozenStudies: 'Studien',
  frozenStudiesOfJob: 'Die des Auftrags',
  noJob: 'Keiner (ohne Annotationen)',
  choice_readers: 'Sets der Befundenden',
  choice_both: 'Konsens und Sets der Befundenden',
  choice_first_readings: 'Erstbefundungen',
  choice_resolution_first_readings: 'Konsens und Erstbefundungen',
  firstReadingsHint: 'Erstbefundungen sind die Sets, die jede befundende Person beim Abschluss ihres Pakets abgab, vor jedem Vergleich; die Sets der Befundenden sind so, wie sie jetzt stehen.',
  disputedTreatment: 'Strittige Befunde',
  disputed_omit: 'Weglassen',
  disputed_include: 'Aufnehmen',
  disputed_ignore: 'Als zu ignorierende Bereiche markieren',
  disputed_share: 'Mit dem Anteil der Befundenden kennzeichnen',
  splitRule: 'Patientinnen und Patienten kommen nach ihrem Aufteilungsfach ins Training (70 %), in die Validierung oder den Test (je 15 %), in jeder eingefrorenen Sammlung gleich. Durch gleiche Pixel verbundene bleiben zusammen.',
  splitConnected: '{count} durch gleiche Pixel verbundene Patientinnen und Patienten hatten Fächer in verschiedenen Mengen; ihre Bilder kamen gemeinsam in die Menge ihrer ersten eingefrorenen Sammlung, sonst ins Training.',
  splitReview: 'Verbundene Patientinnen und Patienten sitzen, wie sich herausstellte, in verschiedenen Mengen dieser eingefrorenen Sammlung. Prüfen Sie sie und die Ergebnisse ihrer Benchmarks, bevor Sie sich darauf stützen.',
  freeze: 'Einfrieren',
  splitCounts: '{train} / {validation} / {test} Bilder',
  excludedImages: 'Ausgeschlossen: {details}',
  excluded_outdated: '{count} mit veraltetem Konsens',
  excluded_unresolved: '{count} ohne Konsens',
  excluded_unread: '{count} nicht befundet',
  exportFormat: 'Format',
  format_coco: 'COCO JSON mit 16-Bit-PNG',
  format_yolo: 'YOLO mit 16-Bit-PNG',
  format_png_cvat: '16-Bit-PNG mit CVAT-1.1-XML',
  format_cvat: 'Nur CVAT-1.1-XML',
  format_dicom: 'DICOM mit den Annotationen im Header',
  leaves_coco: 'COCO lässt Punkte und Linienzüge aus; das Manifest des Exports zählt, was ausgelassen wurde.',
  leaves_yolo: 'YOLO übernimmt nur Rechtecke und lässt Polygone, Linienzüge, Punkte, Masken und Bild-Tags aus; das Manifest des Exports zählt sie.',
  exportAction: 'Exportieren',
  evaluateModel: 'Modell evaluieren',
  evaluationCurrentMap: 'Dieser Lauf ist älter als gespeicherte Klassenzuordnungen; seine Klassen wurden mit der jetzigen Zuordnung des Modells gelesen.',
  evaluationIntro: 'Vergleicht die Vorhersagen eines abgeschlossenen Modelllaufs über den Auftrag dieser eingefrorenen Sammlung mit deren Annotationen: je Bild und Label Sensitivität, Spezifität (95-%-Wilson-Intervalle) und ROC-AUC; für Flächen auch Präzision, Recall und AP bei IoU 0,5. Kurven sehen nur Vorhersagen über der Schwelle des Laufs; starten Sie ihn deshalb mit einer niedrigen.',
  evaluationNeedsRun: 'Zum Evaluieren braucht es einen abgeschlossenen Modelllauf über den Auftrag dieser eingefrorenen Sammlung. Starten Sie ihn auf der Seite des Auftrags.',
  modelRun: 'Modelllauf',
  reference: 'Referenz',
  eachReader: 'Jede:r Befundende',
  threshold: 'Schwelle',
  evaluate: 'Evaluieren',
  breakdown: 'Anzeigen',
  overall: 'Gesamt',
  resultsTooFew: 'Keine Ergebnisse: Ein Ergebnis umfasst mindestens 20 Bilder, ebenso jede Gruppe, in die es aufgeschlüsselt wird.',
  by_age_group: 'Nach Altersgruppe',
  by_sex: 'Nach Geschlecht',
  by_body_part: 'Nach Körperregion',
  by_site: 'Nach Standort',
  downloadJson: 'JSON herunterladen',
  positivesNegatives: 'Mit / ohne',
  sensitivity: 'Sensitivität',
  specificity: 'Spezifität',
  precision: 'Präzision',
  recall: 'Recall',
  split_test: 'Test',
  split_validation: 'Validierung',
  split_train: 'Training',
  split_all: 'Alle',
  reasonNoMajority: 'Keine Mehrheit',
  download: 'Herunterladen',
  readersCoded: 'Lesende codiert',
  readersCodedHint: 'Über eine Freigabe angefordert: Lesende erscheinen als reader-1, reader-2 usw., nicht mit ihrer Nutzer-ID.',
  export_pending: 'In der Warteschlange',
  export_running: 'Wird erstellt',
  export_done: 'Bereit',
  export_failed: 'Fehlgeschlagen',
  shortcuts: 'Tastenkürzel',
  shortcutsIntro: 'Taste wählen, dann die neue drücken. Eine anderweitig belegte Taste wandert hierher. Enter und Esc bleiben fest.',
  shortcutGroup_editing: 'Zeichnen und Bearbeiten',
  shortcutGroup_moving: 'Navigieren',
  shortcutGroup_tools: 'Bild und Werkzeuge',
  shortcutGroup_labels: 'Labels und Tags',
  shortcutKeysOf: '{action}: {keys}. Zum Ändern wählen.',
  shortcutNoKey: 'keine Taste',
  shortcutClear: 'Taste von {action} leeren',
  shortcutsReset: 'Alle Kürzel sind wieder auf ihrem Standard.',
  shortcutsImported: 'Die Kürzel aus der Datei gelten jetzt.',
  shortcutsTouch: 'Legen Sie Ihre Kürzel an einem Gerät mit Tastatur fest; sie gelten für Ihr Konto.',
  change: 'Ändern',
  clear: 'Leeren',
  pressKey: 'Taste drücken … (Esc bricht ab)',
  pressKeyShort: 'Taste drücken',
  shortcutMoved: '{key} ist von {from} hierher gewandert.',
  resetDefaults: 'Standard wiederherstellen',
  exportShortcuts: 'Exportieren',
  importShortcuts: 'Importieren',
  shortcutsInvalid: 'Diese Datei enthält keine Tastenkürzel.',
  command_draw: 'Zeichnen',
  command_select: 'Auswählen',
  command_join: 'Masken verbinden',
  command_smart: 'Smart-Werkzeug',
  command_find: 'Klasse suchen',
  command_previous: 'vorherige Studie',
  command_earlierStudy: 'frühere Studie des Patienten',
  command_laterStudy: 'spätere Studie des Patienten',
  command_next: 'nächste Studie',
  command_otherView: 'andere Ansicht',
  command_undo: 'Rückgängig',
  command_redo: 'Wiederholen',
  command_delete: 'Löschen',
  command_lock: 'Objekt sperren oder entsperren',
  command_zoomIn: 'Vergrößern',
  command_zoomOut: 'Verkleinern',
  command_fit: 'Einpassen',
  command_brushSmaller: 'kleinerer Pinsel',
  command_brushLarger: 'größerer Pinsel',
  command_focus: 'Fokusmodus',
  command_clahe: 'Kontrastanhebung (CLAHE)',
  command_edges: 'Schärfen (Unscharfmaskierung, 200 %)',
  command_crop: 'Zuschnittansicht',
  command_dictate: 'Diktieren (halten)',

  close: 'Schließen',
  annotations: 'Annotationen',
  imageInformation: 'Bildinformationen',
  categoryRectangles: 'Rechtecke',
  categoryPolygons: 'Polygone',
  categoryPolylines: 'Linienzüge',
  categoryPoints: 'Punkte',
  categoryMasks: 'Masken',
  categoryTags: 'Bild-Tags',
  categoryDrawHint: 'Klasse wählen, dann in einem der Bilder zeichnen.',
  categoryTagHint: 'Klick: setzt oder entfernt den Tag im hervorgehobenen Bild. Doppelklick oder Taste zweimal: auf allen Bildern der Studie.',
  keepOpen: 'Offen halten',
  freeText: 'Freitext-Klasse',
  freeTextHelp: 'Für einen Befund, den keine Klasse benennt: einen Namen dafür eingeben. Keine identifizierenden Patientendaten eingeben.',
  freeTextField: 'Freitext-Klasse benennen',
  freeTextNew: 'neue Freitext-Klasse',
  freeTextSuggestions: 'Klassen und verwendete Namen',
  findClassHelp: 'Namen oder Formtyp eingeben. ↑/↓ zum Wählen, Enter zum Übernehmen, Esc zum Schließen. Eine Klasse startet das Zeichnen; ein Bild-Tag wird im aktiven Bild gesetzt.',
  clearSearch: 'Suche leeren',
  classCount: 'Passende Klassen: {count}',
  unassigned: 'Nicht belegt',
  noAnnotationsHere: 'Keine Annotationen in diesem Bild.',
  drawAnnotation: 'Annotation zeichnen',
  changeClass: 'Klasse ändern',
  editDetails: 'Details bearbeiten',
  allDetails: 'Details aller Annotationen',
  expandAllDetails: 'Alle Details aufklappen',
  collapseAllDetails: 'Alle Details zuklappen',
  lockObject: 'Sperren',
  lockHelp: 'Sperren: Das Objekt behält Lage, Klasse und Merkmale, und Zeichnen daneben lässt es unberührt',
  unlockHelp: 'Gesperrt: zum Ändern entsperren',
  lockedByProfile: 'Vom Labeling-Profil gesperrt: zum Ändern ein anderes Profil wählen',
  acceptedFrom: 'Übernommen von {model} ({confidence})',
  tagOnAllViews: 'Auf allen {count} Ansichten der Studie gesetzt',
  tagNotOnAllViews: 'Auf {count} der Ansichten nicht gespeichert: Öffnen Sie sie, um den Grund zu sehen',
  notRated: 'Nicht bewertet',
  subtletyObvious: 'offensichtlich',
  subtletyUnclear: 'unklar',
  subtletySubtle: 'subtil',
  firstSet: 'Erste Bildgruppe',
  lastSet: 'Letzte Bildgruppe',
  setNumber: 'Nummer der aktuellen Bildgruppe',
  setNavigation: 'Navigation durch die Bildgruppen',
  viewsShown: 'Gleichzeitig angezeigte Ansichten',
  previousImage: 'Vorheriges Bild der Studie',
  nextImage: 'Nächstes Bild der Studie',
  readingBrand: 'BEFUNDUNG',
  assignmentMenu: 'Paket',
  imageMenu: 'Bild',
  workspaceViewOnly: 'Nur ansehen',
  assignmentMenuEyebrow: 'BEFUNDUNGSPAKET',
  reviewEyebrow: 'ÜBERPRÜFUNG',
  comparisonEyebrow: 'VERGLEICH',
  adjudicationEyebrow: 'ADJUDIKATION',
  comparison: 'Vergleich',
  datasetEyebrow: 'EINGEFRORENE SAMMLUNG',
  notInSet: 'nicht in diesem Satz',
  noImages: 'Keine Bilder.',
  editShortcuts: 'Tastenkürzel bearbeiten',
  quickGuide: 'Kurzanleitung',
  metricFractures: 'Frakturen',
  metricImages: 'Bilder',
  metricViewSets: 'Bildgruppen',
  viewFilter: 'Ansichtsfilter',
  aiEyebrow: 'ASSISTIERTE ANNOTATION',
  aiReady: 'Bereit',
  aiRunning: 'Läuft',
  aiCount: '{count} gefunden',
  aiRunHelp: 'Das Modell um Vorschläge für alle Ansichten dieser Studie bitten. Nichts wird automatisch übernommen.',
  duplicateDatabase: 'Duplikat-Datenbank',
  dbEyebrow: 'DUPLIKATE',
  archiveEvidence: 'Hinweise im Archiv',
  dbChecking: 'Prüfe',
  dbClear: 'Keine',
  dbFound: '{count} gefunden',
  dbOffline: 'Offline',
  dbNone: 'Keine Kopien oder ähnlichen Bilder dieses Bildes an anderer Stelle der Plattform.',
  readerEyebrow: 'BEFUNDER',
  annotationTools: 'Annotationswerkzeuge',
  resetDisplay: 'Bilddarstellung zurücksetzen',
  exitFocus: 'Fokus beenden',
  finishContour: 'Kontur abschließen',
  activeView: 'AKTIVE ANSICHT',
  activeViewHelp: 'Werkzeuge und Annotationsliste gelten für die hervorgehobene Ansicht.',
  demographicsUnknown: 'Alter und Geschlecht unbekannt',
  guideDrawTitle: 'Klasse wählen und zeichnen',
  guideDraw: 'Auf eine Kategorie in der linken Leiste zeigen, eine Klassentaste drücken oder Klasse suchen (C). Rechteck: erste Ecke klicken, dann die gegenüberliegende (oder ziehen). Konturen: Eckpunkte klicken, dann Doppelklick oder Enter. Esc bricht ab.',
  guideQualifiersTitle: 'Merkmale setzen',
  guideQualifiers: 'Auf eine Frakturbox zeigen oder sie auswählen: ihr Alter ist der Regler darüber, ihre Subtilität (0 offensichtlich, 1 subtil) der Regler darunter. Der Pfeil einer Zeile in der Liste öffnet alle ihre Details, der Pfeil über der Liste die jeder Zeile; ihr Schloss (L) sperrt das Objekt, damit Zeichnen daneben es nie verschiebt oder ändert.',
  guideViewsTitle: 'Beide Ansichten befunden',
  guideViews: 'Bis zu zwei Ansichten einer Studie stehen nebeneinander; der Schalter oben zeigt eine oder zwei, die Pfeile blättern durch eine längere Studie. Q wechselt die hervorgehobene Ansicht; Werkzeuge und Liste gelten für sie. Beim Zeichnen beginnt der erste Klick in der anderen Ansicht dort. Der Filmstreifen über den Ansichten zeigt die Studien des Patienten, die älteste zuerst: Umschalt+← und Umschalt+→ öffnen die frühere und die spätere zum Annotieren, D und F gehen von der Studie aus weiter, von der Sie kamen.',
  guideAdjustTitle: 'Röntgenbild anpassen',
  guideAdjust: 'Ziehen verschiebt; am Bildrand nach oben ziehen vergrößert, nach unten verkleinert; auch das Mausrad zoomt. Leertaste + Ziehen verschiebt überall. Mittlere oder rechte Maustaste ziehen stellt die Fensterung ein; W/L speichert sie für das Bild.',
  guideFinishTitle: 'Bild abschließen',
  guideFinish: 'Änderungen werden automatisch gespeichert. Ist ein Bild befundet, schließen Sie es in der Seitenleiste ab; das nächste offene folgt.',
  command_shortcuts: 'Tastenkürzel bearbeiten',

  role_site_admin: 'Standort-Admin',
  role_coordinator: 'Koordination',
  role_grantee: 'Empfänger:in einer Freigabe',
  role_member: 'Mitglied',
  role_platform_admin: 'Plattform-Admin',
  role_catalog_editor: 'Katalog-Bearbeitung',
  role_educator: 'Lehrende Person',
  role_learner: 'Lernende Person',
  role_reader: 'Befundende Person',
  role_adjudicator: 'Adjudikator:in',
  role_reviewer: 'Prüfende Person',

  markNotice: 'Die Bilder, die Sie sehen, tragen eine unsichtbare Markierung, die Ihr Konto erkennen lässt, sodass sich Kopien zurückverfolgen lassen. Kopieren, fotografieren oder teilen Sie sie nie.',
  markNoticeOk: 'Verstanden',
  imageRenderFailed: 'Das Bild konnte nicht angezeigt werden.',
  errorReference: 'Fehlerreferenz',
  errorDetails: 'Technische Details',
  pageFailed: 'Die Seite konnte nicht angezeigt werden.',
  reloadPage: 'Seite neu laden',
  errorReports: 'Fehlerberichte',
  loadErrorReports: 'Letzte Fehler laden',
  noErrorReports: 'Keine Fehler gefunden.',
  retry: 'Erneut versuchen',
  ...educationDe,
  ...collectionsDe,
  ...catalogDe,
  ...labelingDe,
  ...queryDe,
  ...quickTagDe,
  ...checksDe,
  ...impactDe,
  ...studyTagsDe,
  ...inferenceDe,
  ...benchmarksDe,
  ...contributionsDe,
  ...uploadsDe,
  ...dictationDe,
  ...cloudDe,
  ...forumDe,
  ...schedulingDe,
  ...peopleDe,
  ...sitesDe,
  ...maskJoinDe,
}

const dictionaries = { en, de }
export type Lang = keyof typeof dictionaries
const STORAGE_KEY = 'pedfx.lang'

function initialLang(): Lang {
  const stored = readStored(STORAGE_KEY)
  if (stored === 'en' || stored === 'de') return stored
  return navigator.language.toLowerCase().startsWith('de') ? 'de' : 'en'
}

interface I18n {
  lang: Lang
  setLang: (lang: Lang) => void
  t: (key: MessageKey, vars?: Record<string, string>) => string
  role: (role: SiteRole | 'grantee') => string
  specialtyName: (specialty: Specialty) => string
}

/**
 * A message with its `{name}` variables filled in. Keys built from server values (a state, a
 * notification kind) may be unknown to this build; they show as the key instead of failing.
 */
export function translate(
  dictionary: Readonly<Record<MessageKey, string>>,
  key: MessageKey,
  vars: Record<string, string> = {},
): string {
  // A count of one takes the singular, where one is written as `<key>_one`.
  const singular = vars.count === '1' ? (dictionary as Readonly<Record<string, string>>)[`${key}_one`] : undefined
  const message: string | undefined = singular ?? dictionary[key]
  return (message ?? key).replace(/\{(\w+)\}/g, (_, name: string) => vars[name] ?? `{${name}}`)
}

const I18nContext = createContext<I18n | null>(null)

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang)

  const setLang = useCallback((next: Lang) => {
    setLangState(next)
    writeStored(STORAGE_KEY, next)
  }, [])

  const value = useMemo<I18n>(() => {
    const dictionary = dictionaries[lang]
    const t = (key: MessageKey, vars?: Record<string, string>) => translate(dictionary, key, vars)
    return {
      lang,
      setLang,
      t,
      role: (role) => t(`role_${role}`),
      specialtyName: (specialty) => (lang === 'de' ? specialty.name_de : specialty.name_en),
    }
  }, [lang, setLang])

  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18n {
  const context = useContext(I18nContext)
  if (context === null) throw new Error('useI18n must be used inside I18nProvider')
  return context
}
